/**
 * AuditoriaView V2 -- Auditoria financiera completa con 3 tabs:
 *   1. Comprobantes: Upload + OCR + Validacion + Historial
 *   2. Balance: Resumen diario por medio de pago
 *   3. Colaboradores: Metricas por vendedor/cajero
 *
 * Evolucion AUDITORIA-EVOLUTION-001
 * @module presentation/views/AuditoriaView
 */

import { AuditoriaApiClient }  from '../../infrastructure/api/AuditoriaApiClient.js';
import { confirmadorDeCruce } from '../../application/conciliacion/cruceBanco.js';
import { getSelectedBusiness, getSelectedBusinessName } from '../components/BusinessSelector.js';
import { createActionArea } from '../components/ActionArea.js';
import { crearBandejaPropuestas } from '../components/conciliacion/BandejaPropuestasBanco.js';
import { crearAtribucionBanco } from '../components/conciliacion/AtribucionBanco.js';
import { crearNetosPorNegocio } from '../components/conciliacion/NetosPorNegocio.js';
import { AppConfig } from '../../config.js';
import { avisar, confirmar, pedirTexto } from '../shared/nxDialogo.js';

// ---------------------------------------------------------------------------
// Campos V2 del Comprobante Z
// ---------------------------------------------------------------------------

const CAMPOS_MONETARIOS = [
  { key: 'ventas',          label: 'Ventas',           grupo: 'operaciones' },
  { key: 'devolucion',      label: 'Devolucion',       grupo: 'operaciones', esNegativo: true },
  { key: 'total_neto',      label: 'Total neto',       grupo: 'operaciones', esTotal: true },
  { key: 'financiero',      label: 'Financiero',       grupo: 'operaciones' },
  { key: 'recogidas',       label: 'Recogidas',        grupo: 'operaciones' },
  { key: 'fondo_inicial',   label: 'Fondo inicial',    grupo: 'operaciones' },
  { key: 'descuentos',      label: 'Descuentos',       grupo: 'adicional' },
  { key: 'ventas_gravadas', label: 'Ventas gravadas',  grupo: 'adicional' },
  { key: 'iva_generado',    label: 'IVA generado 19%', grupo: 'adicional' },
  { key: 'ventas_exentas',  label: 'Exentas',          grupo: 'adicional' },
  { key: 'ventas_excluidas',label: 'Excluidas',        grupo: 'adicional' },
  { key: 'ca_ttic',         label: 'C.A./T.I.C.',      grupo: 'adicional', esTotal: true },
  { key: 'efectivo',        label: 'Efectivo',         grupo: 'medios' },
  { key: 'tarjetas_dc',     label: 'Tarjetas D/C',     grupo: 'medios' },
  { key: 'qr_bancolombia',  label: 'QR Bancolombia',   grupo: 'medios' },
  { key: 'sistecredito',    label: 'SisteCredito',     grupo: 'medios' },
  { key: 'intercredito',    label: 'InterCredito',     grupo: 'medios' },
  { key: 'addi',            label: 'ADDI',             grupo: 'medios' },
  { key: 'bono_datafono',   label: 'Bono Datafono',    grupo: 'medios' },
];

const CAMPOS_INFO = [
  { key: 'vendedor',         label: 'Vendedor',        type: 'text' },
  { key: 'fecha',            label: 'Fecha',           type: 'date' },
  { key: 'establecimiento',  label: 'Establecimiento', type: 'text' },
  { key: 'caja',             label: 'Caja',            type: 'text' },
  { key: 'hora_cierre',      label: 'Hora cierre',     type: 'time' },
  { key: 'numero_cierre',    label: '# Cierre',        type: 'number' },
  { key: 'serie_pos',        label: 'Serie POS',       type: 'text' },
  { key: 'terminal_id',      label: 'Terminal',        type: 'text' },
  { key: 'num_facturas',     label: 'Facturas',        type: 'number' },
  { key: 'num_factura_inicio',label: '1ra Factura',    type: 'number' },
  { key: 'num_factura_fin',  label: 'Ultima Factura',  type: 'number' },
  { key: 'num_articulos',    label: 'Articulos',       type: 'number' },
  { key: 'stock_vendido',    label: 'Stock vendido',   type: 'number' },
  { key: 'stock_devuelto',   label: 'Stock devuelto',  type: 'number' },
  { key: 'stock_neto',       label: 'Stock neto',      type: 'number' },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function fmt(val) {
  if (val == null || val === '') return '—';
  const n = parseFloat(val);
  if (isNaN(n)) return val;
  return new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(n);
}

// Deuda 4: este escape (no cambiaba la comilla simple) es ahora el compartido.
import { esc } from '../shared/texto.js';
import { regresoHtml } from '../shared/regreso.js';

function filePreviewUrl(file) {
  return URL.createObjectURL(file);
}

// ---------------------------------------------------------------------------
// Limite de tamano + compresion automatica
// ---------------------------------------------------------------------------
// El workflow n8n de auditoria corta en 4 MB (N8N_PAYLOAD_SIZE_MAX).
// Para imagenes, se reescala a 2048px y baja calidad JPEG hasta caber.
// Para PDFs (sin compresion en browser) se pide al usuario reducir antes.
const AUD_MAX_UPLOAD_MB = 4;
const AUD_MAX_UPLOAD_BYTES = AUD_MAX_UPLOAD_MB * 1024 * 1024;

function loadImageElement(file) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = (e) => { URL.revokeObjectURL(url); reject(e); };
    img.src = url;
  });
}

async function compressImageToFit(file, maxBytes, longSidePx = 2048) {
  const img = await loadImageElement(file);
  let { naturalWidth: w, naturalHeight: h } = img;
  const max = Math.max(w, h);
  if (max > longSidePx) {
    const scale = longSidePx / max;
    w = Math.round(w * scale);
    h = Math.round(h * scale);
  }
  const canvas = document.createElement('canvas');
  canvas.width = w; canvas.height = h;
  canvas.getContext('2d').drawImage(img, 0, 0, w, h);

  let quality = 0.85;
  let blob = null;
  for (let i = 0; i < 6; i++) {
    blob = await new Promise(r => canvas.toBlob(r, 'image/jpeg', quality));
    if (blob && blob.size <= maxBytes) break;
    quality -= 0.15;
    if (quality < 0.35) break;
  }
  if (!blob || blob.size > maxBytes) {
    throw new Error('No se pudo comprimir la imagen por debajo del limite');
  }
  const newName = file.name.replace(/\.[^.]+$/, '') + '_compressed.jpg';
  return new File([blob], newName, { type: 'image/jpeg', lastModified: Date.now() });
}

// ---------------------------------------------------------------------------
// Sub-renders
// ---------------------------------------------------------------------------

function renderAlertas(validacion) {
  if (!validacion?.alertas?.length) return '';
  return `
    <div class="aud-alertas-section">
      <div class="aud-alertas-title">Alertas de validacion (${validacion.total_alertas})</div>
      ${validacion.alertas.map(a => `
        <div class="aud-alerta-item">
          <span class="aud-alerta-tipo">${esc(a.tipo)}</span>
          <span>${esc(a.msg)}</span>
        </div>
      `).join('')}
    </div>`;
}

function renderMovimientos(movimientos) {
  if (!movimientos?.length) return '';
  return `
    <div class="aud-mov-section">
      <div class="aud-col-heading">Movimientos de caja (pagina 2)</div>
      ${movimientos.map(m => `
        <div class="aud-mov-item">
          <span class="aud-mov-tipo ${m.direccion === 'OUT' ? 'aud-mov-out' : 'aud-mov-in'}">${esc(m.tipo || m.direccion)}</span>
          <span style="flex:1">${esc(m.concepto || m.proveedor || '—')}</span>
          <span style="font-family:monospace;font-weight:600">${fmt(m.monto)}</span>
        </div>
      `).join('')}
    </div>`;
}

// Mapeo de tipo de check/alerta → columna UI donde debe aparecer
// 'identificacion' = columna izquierda (campos info, stock, facturas)
// 'operaciones' = grupo Operaciones del dia (columna derecha)
// 'adicional' = grupo Datos adicionales (columna derecha)
// 'medios' = grupo Medios de pago (columna derecha)
const TIPO_A_COLUMNA = {
  // Izquierda (identificacion)
  'STOCK_INCONSISTENTE': 'identificacion',
  'RANGO_FACTURAS_INCONSISTENTE': 'identificacion',
  'CIERRE_SIN_FACTURAS': 'identificacion',
  // Derecha / Operaciones
  'NETO_INCONSISTENTE': 'operaciones',
  'DEVOLUCION_ALTA': 'operaciones',
  'GASTOS_SIN_SALIDA': 'operaciones',
  // Derecha / Adicional
  'IVA_INCONSISTENTE': 'adicional',
  'DESCUENTOS_ALTO': 'adicional',
  // Derecha / Medios
  'PAGOS_NO_CUADRAN': 'medios'
};

// Identificar check positivo por contenido del mensaje
function checkColumna(check) {
  const msg = (check.msg || '').toLowerCase();
  if (msg.includes('stock') || msg.includes('facturas:')) return 'identificacion';
  if (msg.includes('ventas - devoluciones') || msg.includes('neto')) return 'operaciones';
  if (msg.includes('iva') || msg.includes('descuentos') || msg.includes('c.a.t.t')) return 'adicional';
  if (msg.includes('medios de pago')) return 'medios';
  return check.seccion || 'operaciones';
}

function renderChecks(checks, columna) {
  const items = (checks || []).filter(c => checkColumna(c) === columna);
  if (!items.length) return '';
  return items.map(c => `
    <div class="aud-check-ok">
      <span class="aud-check-icon">OK</span>
      <span>${esc(c.msg)}</span>
    </div>`).join('');
}

function renderAlertasSeccion(alertas, columna) {
  const items = (alertas || []).filter(a => (TIPO_A_COLUMNA[a.tipo] || a.seccion) === columna);
  if (!items.length) return '';
  return items.map(a => `
    <div class="aud-check-fail">
      <span class="aud-check-icon-fail">!</span>
      <span>${esc(a.msg)}</span>
    </div>`).join('');
}

// NOTA V2.3: Los workarounds locales (recomputePagosCheck, checkHoraNominal) fueron
// removidos porque ahora el workflow n8n V2.3 ya aplica la formula correcta
// (sum_medios = neto + financiero) y detecta la hora nominal automaticamente.

// Mapping: snake_case (OCR) → camelCase (API DTO)
// El workflow n8n devuelve campos snake_case, pero el API C# espera camelCase.
const CIERRE_Z_FIELD_MAP = {
  fecha: 'fechaCierre',
  hora_cierre: 'horaCierre',
  numero_cierre: 'numeroCierre',
  num_facturas: 'numFacturas',
  num_articulos: 'numArticulos',
  num_factura_inicio: 'numFacturaInicio',
  num_factura_fin: 'numFacturaFin',
  stock_vendido: 'stockVendido',
  stock_devuelto: 'stockDevuelto',
  stock_neto: 'stockNeto',
  total_neto: 'totalNeto',
  ventas_gravadas: 'ventasGravadas',
  iva_generado: 'ivaGenerado',
  ventas_exentas: 'ventasExentas',
  ventas_excluidas: 'ventasExcluidas',
  ca_ttic: 'caTtic',
  tarjetas_dc: 'tarjetasDc',
  bono_datafono: 'bonoDatafono',
  qr_bancolombia: 'qrBancolombia',
  serie_pos: 'seriePos',
  terminal_id: 'terminalId',
  fondo_inicial: 'fondoInicial'
};

function mapCierreZForApi(data) {
  const out = {};
  for (const [key, val] of Object.entries(data || {})) {
    if (val == null || val === '') continue;
    const mapped = CIERRE_Z_FIELD_MAP[key] || key;
    out[mapped] = val;
  }
  return out;
}

// Calcular validaciones localmente al editar (para revalidacion en tiempo real)
function recomputeValidaciones(data) {
  const alertas = [];
  const checks = [];

  const num = (k) => {
    const v = parseFloat(data[k]);
    return isNaN(v) ? null : v;
  };

  const ventas = num('ventas');
  const devolucion = num('devolucion');
  const totalNeto = num('total_neto');
  const financiero = num('financiero') || 0;
  const ivaGenerado = num('iva_generado');
  const ventasGravadas = num('ventas_gravadas');
  const descuentos = num('descuentos');
  const caTtic = num('ca_ttic');

  // 1. Neto = Ventas - Devolucion
  if (ventas != null && totalNeto != null) {
    const dev = devolucion || 0;
    const expected = ventas - dev;
    if (Math.abs(totalNeto - expected) > 100) {
      alertas.push({ tipo: 'NETO_INCONSISTENTE', seccion: 'operaciones',
        msg: 'Total neto no es ventas - devolucion' });
    } else {
      checks.push({ seccion: 'operaciones', msg: 'Ventas - Devoluciones = Neto' });
    }
  }

  // 2. IVA = Gravadas * 19%
  if (ivaGenerado != null && ventasGravadas != null) {
    const expected = Math.round(ventasGravadas * 0.19);
    if (Math.abs(ivaGenerado - expected) > 500) {
      alertas.push({ tipo: 'IVA_INCONSISTENTE', seccion: 'adicional',
        msg: 'IVA no coincide con ventas gravadas x 19%' });
    } else {
      checks.push({ seccion: 'adicional', msg: 'IVA = Gravadas x 19%' });
    }
  }

  // 3. Pagos = Neto + Financiero
  const sumMedios = (num('efectivo') || 0) + (num('tarjetas_dc') || 0) +
    (num('sistecredito') || 0) + (num('bono_datafono') || 0) +
    (num('addi') || 0) + (num('qr_bancolombia') || 0) + (num('intercredito') || 0);
  const esperadoMedios = (totalNeto || 0) + financiero;
  if (esperadoMedios > 0 && Math.abs(sumMedios - esperadoMedios) > 500) {
    alertas.push({ tipo: 'PAGOS_NO_CUADRAN', seccion: 'medios',
      msg: 'Suma medios de pago no cuadra con total neto + financiero' });
  } else if (esperadoMedios > 0) {
    checks.push({ seccion: 'medios', msg: 'Suma medios = Neto + Financiero' });
  }

  // 4. Stock
  // El OCR puede extraer "stock_devuelto" como valor negativo porque asi aparece en el
  // recibo fisico (ej. "-2" en la columna devuelto, que significa "salieron 2 del stock").
  // La formula correcta es: neto = vendido - |devuelto| (valor absoluto). Antes hacia
  // vendido - devuelto que para devuelto=-2 daba 30 - (-2) = 32 != 28 (falso positivo).
  const sv = num('stock_vendido');
  const sd = num('stock_devuelto');
  const sn = num('stock_neto');
  if (sv != null && sd != null && sn != null) {
    const devAbs = Math.abs(sd);
    if (sn !== sv - devAbs) {
      alertas.push({ tipo: 'STOCK_INCONSISTENTE', seccion: 'identificacion',
        msg: 'Stock neto no es vendido - devuelto' });
    } else {
      checks.push({ seccion: 'identificacion', msg: 'Stock vendido - devuelto = neto' });
    }
  }

  // 5. Rango facturas
  const nfi = num('num_factura_inicio');
  const nff = num('num_factura_fin');
  const nf = num('num_facturas');
  if (nfi != null && nff != null && nf != null) {
    const rango = nff - nfi + 1;
    if (rango > 0 && Math.abs(rango - nf) > 1) {
      alertas.push({ tipo: 'RANGO_FACTURAS_INCONSISTENTE', seccion: 'identificacion',
        msg: 'Rango de facturas no coincide con cantidad (diferencia > 1)' });
    } else if (rango > 0) {
      checks.push({ seccion: 'identificacion',
        msg: `Facturas: ${nfi} a ${nff} (${rango} posiciones, ${nf} reportadas)` });
    }
  }

  // 6. Descuentos (> 5%)
  if (descuentos != null && ventas != null && ventas > 0) {
    const pct = (descuentos / ventas * 100);
    if (pct > 5) {
      alertas.push({ tipo: 'DESCUENTOS_ALTO', seccion: 'adicional',
        msg: `Descuentos superan el 5% de ventas (${pct.toFixed(1)}%)` });
    } else {
      checks.push({ seccion: 'adicional', msg: `Descuentos dentro de rango (${pct.toFixed(1)}%)` });
    }
  }

  // 7. Devoluciones (> 3%)
  if (devolucion != null && ventas != null && ventas > 0) {
    const pct = (devolucion / ventas * 100);
    if (pct > 3) {
      alertas.push({ tipo: 'DEVOLUCION_ALTA', seccion: 'operaciones',
        msg: `Devoluciones superan el 3% de ventas (${pct.toFixed(1)}%)` });
    }
  }

  // 8. C.A.T.T.I.C = Total neto
  if (caTtic != null && totalNeto != null && Math.abs(caTtic - totalNeto) < 100) {
    checks.push({ seccion: 'adicional', msg: 'C.A.T.T.I.C = Total neto' });
  }

  // 9. Hora nominal
  if (data.hora_cierre === '12:00' || data.hora_cierre === '00:00') {
    alertas.push({ tipo: 'HORA_NOMINAL', seccion: 'identificacion',
      msg: 'Hora de cierre parece ser la nominal del sistema' });
  }

  return { alertas, checks, total_alertas: alertas.length, total_checks: checks.length };
}

function renderExtractedForm(data) {
  // V2.3: el workflow n8n ya aplica todas las validaciones correctas, no recomputamos
  const val = data?.validacion || { checks: [], alertas: [], total_alertas: 0, total_checks: 0 };

  const infoRows = CAMPOS_INFO.map(f => {
    const v = data?.[f.key] ?? '';
    return `
      <div class="aud-field-row">
        <label class="aud-field-label">${esc(f.label)}</label>
        <input class="aud-field-input form-control form-control-sm"
          type="${f.type}" name="${f.key}" value="${esc(v)}" placeholder="—">
      </div>`;
  }).join('');

  const grupoLabels = { operaciones: 'Operaciones del dia', adicional: 'Datos adicionales', medios: 'Medios de pago' };
  let prevGrupo = '';
  let monRows = '';
  CAMPOS_MONETARIOS.forEach(f => {
    const v = data?.[f.key] ?? '';
    const numV = parseFloat(v);
    const fmtV = !isNaN(numV) ? fmt(numV) : '';
    if (f.grupo !== prevGrupo) {
      // Close previous group validation — usando columna en vez de seccion
      if (prevGrupo) {
        monRows += `<div class="aud-group-checks">${renderChecks(val.checks, prevGrupo)}${renderAlertasSeccion(val.alertas, prevGrupo)}</div>`;
      }
      monRows += `<div class="aud-group-sep">${grupoLabels[f.grupo]}</div>`;
      prevGrupo = f.grupo;
    }
    const cls = f.esTotal ? ' aud-field-total' : f.esNegativo ? ' aud-field-neg' : '';
    monRows += `
      <div class="aud-field-row${cls}">
        <label class="aud-field-label">${esc(f.label)}</label>
        <div class="aud-field-money-wrap">
          <input class="aud-field-input aud-field-money form-control form-control-sm"
            type="number" step="1" name="${f.key}" value="${esc(v)}" placeholder="0">
          <span class="aud-field-fmt">${fmtV}</span>
        </div>
      </div>`;
  });
  // Close last group validation
  if (prevGrupo) {
    monRows += `<div class="aud-group-checks">${renderChecks(val.checks, prevGrupo)}${renderAlertasSeccion(val.alertas, prevGrupo)}</div>`;
  }

  return `
    <div class="aud-extracted-wrap">
      ${renderAlertas(data?.validacion)}
      <div class="aud-section-title">Datos extraidos — verifica antes de guardar</div>
      <form id="aud-confirm-form" class="aud-form-cols">
        <div class="aud-col-info">
          <div class="aud-col-heading">Identificacion del comprobante</div>
          ${infoRows}
          <div class="aud-group-checks">${renderChecks(val.checks, 'identificacion')}${renderAlertasSeccion(val.alertas, 'identificacion')}</div>
        </div>
        <div class="aud-col-money">
          ${monRows}
        </div>
      </form>
      ${renderMovimientos(data?.movimientos)}
      <div class="aud-form-actions">
        <button class="btn btn-ghost btn-sm" id="aud-btn-discard">Descartar</button>
        <button class="btn btn-primary" id="aud-btn-save">Guardar en BD</button>
      </div>
    </div>`;
}

function renderHistorial(items) {
  if (!items?.length) {
    return `<div class="aud-hist-empty">No hay registros anteriores.</div>`;
  }
  const rows = items.map((r, idx) => {
    // Leer tanto camelCase (API C#) como snake_case (legacy)
    const fecha = r.fechaCierre ?? r.fecha_cierre ?? r.fecha ?? '—';
    const numCierre = r.numeroCierre ?? r.numero_cierre ?? '—';
    const totalNeto = r.totalNeto ?? r.total_neto;
    const neto = totalNeto != null ? fmt(totalNeto) : '—';
    const estado = r.estado ?? 'guardado';
    const alertas = r.alertasCount ?? r.alertas_count ?? 0;
    const estadoCls = estado === 'guardado' ? 'aud-badge-ok' : 'aud-badge-pend';
    const alertaCls = alertas > 0 ? (alertas > 2 ? 'aud-badge-danger' : 'aud-badge-warn') : '';
    return `<tr class="aud-hist-row-clickable" data-idx="${idx}" title="Click para abrir">
      <td>${esc(fecha)}</td>
      <td>${esc(numCierre)}</td>
      <td>${esc(r.caja ?? '—')}</td>
      <td>${esc(r.vendedor ?? '—')}</td>
      <td class="aud-td-right">${neto}</td>
      <td>${alertas > 0 ? `<span class="aud-badge ${alertaCls}">${alertas} alertas</span>` : ''}</td>
      <td><span class="aud-badge ${estadoCls}">${esc(estado)}</span></td>
      <td class="aud-td-center">
        <button class="aud-btn-delete" data-idx="${idx}" data-id="${esc(r.id ?? '')}" title="Eliminar cierre">✕</button>
      </td>
    </tr>`;
  }).join('');

  return `
    <table class="aud-hist-table">
      <thead>
        <tr>
          <th>Fecha</th><th>Cierre #</th><th>Caja</th><th>Vendedor</th>
          <th class="aud-td-right">Total neto</th><th>Alertas</th><th>Estado</th><th></th>
        </tr>
      </thead>
      <tbody>${rows}</tbody>
    </table>`;
}

function renderMetricas(items) {
  if (!items?.length) return `<div class="aud-hist-empty">No hay datos de colaboradores.</div>`;

  return `<div class="aud-metricas-grid">
    ${items.map(m => {
      const pctDesc = parseFloat(m.pctDescuentoPromedio || 0);
      const pctDev = parseFloat(m.pctDevolucionPromedio || 0);
      const descCls = pctDesc > 5 ? 'danger' : pctDesc > 3 ? 'warn' : '';
      const devCls = pctDev > 3 ? 'danger' : pctDev > 1 ? 'warn' : '';
      const alertas = m.cierresConAlertas || 0;
      return `
      <div class="aud-metrica-card">
        <div class="aud-metrica-nombre">
          ${esc(m.vendedor || 'Sin nombre')}
          ${alertas > 0 ? `<span class="aud-badge aud-badge-warn">${alertas} alertas</span>` : `<span class="aud-badge aud-badge-ok">OK</span>`}
        </div>
        <div class="aud-metrica-stats">
          <div class="aud-metrica-stat"><span>Cierres</span><span class="aud-metrica-val">${m.totalCierres || 0}</span></div>
          <div class="aud-metrica-stat"><span>Ventas totales</span><span class="aud-metrica-val">${fmt(m.ventasTotales)}</span></div>
          <div class="aud-metrica-stat"><span>Venta promedio</span><span class="aud-metrica-val">${fmt(m.ventaPromedio)}</span></div>
          <div class="aud-metrica-stat"><span>% Descuentos</span><span class="aud-metrica-val ${descCls}">${pctDesc.toFixed(1)}%</span></div>
          <div class="aud-metrica-stat"><span>% Devoluciones</span><span class="aud-metrica-val ${devCls}">${pctDev.toFixed(1)}%</span></div>
          <div class="aud-metrica-stat"><span>Periodo</span><span class="aud-metrica-val" style="font-size:0.875rem">${esc(m.primerCierre || '—')} a ${esc(m.ultimoCierre || '—')}</span></div>
        </div>
      </div>`;
    }).join('')}
  </div>`;
}

// ---------------------------------------------------------------------------
// Vista principal
// ---------------------------------------------------------------------------

export function AuditoriaView() {
  const businessId   = getSelectedBusiness();
  const businessName = getSelectedBusinessName();

  let uploadedFile   = null;
  let previewUrl     = null;
  let extractedData  = null;
  let extractedTipo  = null; // 'cierre_z' | 'conciliacion_externa'
  let extractedConfianza = 0;
  let historialItems = [];
  let balanceItems   = [];
  let metricasItems  = [];
  let conciliacionesItems = [];
  // Cierre Z especifico que cuadra con el monto/fecha del form de conciliacion (set por matchConciliacionConZ).
  // null cuando no hay match exacto (entonces el save no vincula auditoria_z_id).
  let matchedZForConciliacion = null;
  // Set de fechas expandidas en la tabla de Balance (subfilas por caja con conciliacion vinculada).
  const expandedBalanceDates = new Set();
  let phase          = 'upload';
  let activeTab      = 'comprobantes';
  let balanceDesde   = '';
  let balanceHasta   = '';
  // CB-T4: estado de la pestana Conciliacion banco (bank_movements). Scaffold.
  let bancoSubtab    = 'upload'; // 'upload' | 'propuestas' | 'revision' | 'rolling' | 'historial'
  // BAN-BANDEJA-PROPUESTAS-T1: la conciliacion por dia que propone Argemiro; decide solo el rol admin ([RequireAdmin]).
  // Se crea una vez: su estado (mes, decisiones de la sesion) sobrevive a los render() de la vista.
  const bandejaPropuestas = crearBandejaPropuestas({ businessId, puedeDecidir: AppConfig.role === 'admin' });
  // BAN-T3: atribuir cada movimiento de una cuenta compartida (EC / Nexo / personal). Su subpestana aparece SOLO si el
  // servidor dice multiNegocio y el rol es admin: un negocio solo nunca ve el selector.
  const atribucionBanco = crearAtribucionBanco({ businessId, puedeDecidir: AppConfig.role === 'admin' });
  // T5: cuanto entro y salio para cada negocio de la cuenta; aparece junto a la atribucion (misma condicion).
  const netosPorNegocio = crearNetosPorNegocio({ businessId, destinos: () => atribucionBanco.estado.destinos });
  // CONCILIA-UI (task 9736eb89): saldo rodante banco vs sistema (CB-T3, endpoint sin UI
  // hasta hoy). El desfase acumulado se MUESTRA con su explicacion (fantasma + egresos
  // sin registrar), no se esconde. Golden EC: base 2.000.000 -> desfase 801.588.
  let bancoRolling = {
    base: 2000000,
    desde: new Date(Date.now() - 30 * 24 * 3600 * 1000).toISOString().slice(0, 10),
    hasta: new Date().toISOString().slice(0, 10),
    data: null, loading: false, error: null
  };
  let bancoFile      = null;     // .xlsx seleccionado (aun no se parsea: backend CB-T1 pendiente)
  let bancoActionArea = null;    // instancia ActionArea reusada para match-review
  let bancoHistItems = [];       // historial conciliacion banco (placeholder)
  let bancoMatchData = null;     // #2478 item2: payload match completo (todos los dias)
  let bancoDay       = 'todos';  // #2478 item2: dia seleccionado para revisar ('todos' | 'YYYY-MM-DD')
  let bancoRevItems  = [];       // #2495 frente1: movimientos requiere-revision (bandeja)
  let bancoRevAll    = [];       // #2531 f4: TODOS los movimientos del match (para desglose consistente)
  let bancoRevResumen = null;    // #2531 f4: resumen backend (items_total, reportadas_no_llegaron)

  // #2478 item1: labels de match-review en modo conciliacion (no compras/inventario).
  const CB_MATCH_LABELS = {
    header:   '🏦 Revisar conciliacion bancaria',
    intro:    'Confirma como cruza cada movimiento del banco contra ventas por transferencia y egresos.',
    itemNoun: 'movimiento',
    confirm:  'Confirmar conciliacion',
    apply:    'Aplicar conciliacion',
    // #2531 f3: matar todo texto de compras/inventario/factura en modo conciliacion.
    fieldLabel:        'Pago del sistema',
    selectPlaceholder: '-- Selecciona pago del sistema --',
    actionLabels:      { MATCH: 'Conciliar', CREATE: 'Crear egreso', REVIEW: 'Revisar' },
    summaryHint:       'Revisa como cruza cada movimiento antes de aplicar. Los no confirmados quedan pendientes.',
    showInventory:     false,
    confirmado:        'Conciliacion guardada',
    confirmadoComo:    'conciliado',
    exitConfirm: (n) =>
      `Faltan ${n} movimiento(s) por confirmar.\n\n` +
      `Si cierras ahora, los movimientos no confirmados quedan pendientes (no se aplican) hasta que los revises.\n\n` +
      `¿Cerrar de todos modos?`
  };

  const container = document.createElement('div');
  container.className = 'view-container';

  function render() {
    container.innerHTML = `
      <div class="view-header">
        ${regresoHtml()}
        <div class="view-header-top">
          <h2 class="view-title">Auditoria</h2>
          <span class="view-subtitle">${esc(businessName || businessId)}</span>
        </div>
      </div>

      ${!businessId ? `<div class="empty-state"><p>Selecciona un negocio primero.</p></div>` : `
        <div class="aud-layout">
          <div class="aud-tabs">
            <button class="aud-tab${activeTab === 'comprobantes' ? ' active' : ''}" data-tab="comprobantes">Comprobantes</button>
            <button class="aud-tab${activeTab === 'conciliacion' ? ' active' : ''}" data-tab="conciliacion">Conciliacion externa</button>
            <button class="aud-tab${activeTab === 'balance' ? ' active' : ''}" data-tab="balance">Balance</button>
            <button class="aud-tab${activeTab === 'colaboradores' ? ' active' : ''}" data-tab="colaboradores">Colaboradores</button>
            <button class="aud-tab${activeTab === 'conciliacion-banco' ? ' active' : ''}" data-tab="conciliacion-banco">Conciliacion banco</button>
          </div>

          ${activeTab === 'comprobantes' ? renderTabComprobantes() : ''}
          ${activeTab === 'conciliacion' ? renderTabConciliacion() : ''}
          ${activeTab === 'balance' ? renderTabBalance() : ''}
          ${activeTab === 'colaboradores' ? renderTabColaboradores() : ''}
          ${activeTab === 'conciliacion-banco' ? renderTabConciliacionBanco() : ''}
        </div>
      `}`;

    bindEvents();
  }

  function renderTabComprobantes() {
    return `
      <section class="aud-upload-section" id="aud-upload-section">
        <div class="aud-section-title">Subir documento para auditoria</div>
        <p style="font-size:0.875rem;color:#8b949e;margin-top:-.5rem;margin-bottom:.75rem">
          El sistema detectara automaticamente si es un <strong>Comprobante Z</strong> de cierre de caja o un <strong>Informe de conciliacion externa</strong> (Redeban / CCO / Bancolombia).
        </p>
        <div class="aud-drop-zone${phase === 'extracting' ? ' aud-drop-loading' : ''}" id="aud-drop-zone">
          ${phase === 'extracting' ? `
            <div class="spinner"></div>
            <p class="aud-drop-hint">Realizando validaciones de seguridad con IA, estamos analizando el contenido del comprobante...</p>
          ` : uploadedFile ? `
            ${uploadedFile.type.startsWith('image/') ? `
              <img src="${previewUrl}" class="aud-preview-img" alt="preview">
            ` : `
              <div class="aud-pdf-icon">PDF</div>
              <p class="aud-drop-name">${esc(uploadedFile.name)}</p>
            `}
            <p class="aud-drop-hint">${esc(uploadedFile.name)} - ${(uploadedFile.size / 1024).toFixed(0)} KB</p>
          ` : `
            <div class="aud-drop-icon">+</div>
            <p class="aud-drop-main">Arrastra aqui o haz clic</p>
            <p class="aud-drop-hint">Cierre Z o Informe de conciliacion (JPG/PNG/PDF)</p>
          `}
          <input type="file" id="aud-file-input" accept="image/*,.pdf" style="display:none">
        </div>
        ${phase === 'upload' ? `
          <div class="aud-upload-actions">
            <button class="btn btn-ghost btn-sm" id="aud-btn-browse">Explorar archivos</button>
            ${uploadedFile ? `<button class="btn btn-primary" id="aud-btn-extract">Extraer datos</button>` : ''}
          </div>
        ` : ''}
        ${phase === 'review' || phase === 'saved' ? `
          <button class="btn btn-ghost btn-sm" id="aud-btn-nueva" style="margin-top:.5rem">+ Nuevo documento</button>
        ` : ''}
      </section>

      ${(phase === 'review' || phase === 'saving') && extractedData && extractedTipo === 'cierre_z' ? `
        <section class="aud-review-section" id="aud-review-section">
          <div class="aud-check-ok" style="margin-bottom:.75rem">
            <span class="aud-check-icon">IA</span>
            <span>Validado como <strong>Comprobante Z</strong> (confianza ${extractedConfianza}%). Verifica antes de guardar.</span>
          </div>
          ${renderExtractedForm(extractedData)}
          ${phase === 'saving' ? `<div class="aud-saving-overlay"><div class="spinner"></div></div>` : ''}
        </section>
      ` : ''}
      ${phase === 'review' && extractedTipo === 'conciliacion_externa' ? `
        <div class="aud-check-ok" style="margin-top:1rem">
          <span class="aud-check-icon">IA</span>
          <span>Validado como <strong>Informe de conciliacion externa</strong>. Te movimos a la pestana "Conciliacion externa" para que revises los datos.</span>
        </div>
      ` : ''}

      <section class="aud-hist-section">
        <div class="aud-section-title">
          Historial de comprobantes
          ${phase === 'saved' ? `<span class="aud-badge aud-badge-ok" style="margin-left:.75rem">Guardado</span>` : ''}
        </div>
        <div id="aud-hist-content">${renderHistorial(historialItems)}</div>
      </section>`;
  }

  function renderTabBalance() {
    return `
      <section class="aud-upload-section">
        <div class="aud-section-title">Balance por periodo</div>
        <div class="aud-balance-filters">
          <label style="font-size:0.875rem;color:#8b949e">Desde:</label>
          <input type="date" id="aud-bal-desde" value="${balanceDesde}">
          <label style="font-size:0.875rem;color:#8b949e">Hasta:</label>
          <input type="date" id="aud-bal-hasta" value="${balanceHasta}">
          <button class="btn btn-ghost btn-sm" id="aud-btn-balance">Consultar</button>
        </div>
        <div id="aud-balance-content">${renderBalance(balanceItems)}</div>
      </section>`;
  }

  function renderTabColaboradores() {
    return `
      <section class="aud-upload-section">
        <div class="aud-section-title">Metricas por colaborador</div>
        <div id="aud-metricas-content">${renderMetricas(metricasItems)}</div>
      </section>`;
  }

  function renderTabConciliacion() {
    // Pre-rellenar desde datos extraidos si el tipo es conciliacion_externa
    const autoFilled = (extractedTipo === 'conciliacion_externa' && extractedData) ? extractedData : null;

    const autoAlerts = autoFilled?.validacion?.alertas?.length
      ? renderAlertas(autoFilled.validacion)
      : '';

    const autoBadge = autoFilled
      ? `<div class="aud-check-ok" style="margin-bottom:.75rem">
          <span class="aud-check-icon">IA</span>
          <span>Datos validados automaticamente del informe (confianza ${extractedConfianza}%). Verifica antes de guardar.</span>
        </div>`
      : '';

    return `
      <section class="aud-upload-section">
        <div class="aud-section-title">Conciliacion externa (Redeban / CCO / Bancolombia)</div>
        <p style="font-size:0.875rem;color:#8b949e;margin-bottom:1rem">
          Sube la imagen/PDF del informe del datafono en la pestana Comprobantes y el sistema lo detectara automaticamente. Tambien puedes ingresar los datos manualmente aqui.
          El sistema verificara si los totales cuadran con las Tarjetas D/C del cierre Z del mismo dia.
        </p>

        ${autoBadge}
        ${autoAlerts}

        <form id="aud-conc-form" class="aud-form-cols">
          <div class="aud-col-info">
            <div class="aud-col-heading">Datos del informe</div>

            <div class="aud-field-row">
              <label class="aud-field-label">Fecha</label>
              <input type="date" name="fecha" class="aud-field-input form-control form-control-sm" value="${esc(autoFilled?.fecha || '')}" required>
            </div>

            <div class="aud-field-row">
              <label class="aud-field-label">Proveedor</label>
              <select name="proveedor" class="aud-field-input form-control form-control-sm" required>
                <option value="">-- Seleccionar --</option>
                ${['REDEBAN','CCO','BANCOLOMBIA','VISA','MASTERCARD','ADDI','INTERCREDITO','SISTECREDITO'].map(p =>
                  `<option value="${p}" ${autoFilled?.proveedor === p ? 'selected' : ''}>${p}</option>`
                ).join('')}
              </select>
            </div>

            <div class="aud-field-row">
              <label class="aud-field-label">Terminal</label>
              <input type="text" name="terminal_id" class="aud-field-input form-control form-control-sm" value="${esc(autoFilled?.terminal_id || '')}" placeholder="000BFDA4">
            </div>

            <div class="aud-field-row">
              <label class="aud-field-label">Lote / CU</label>
              <input type="text" name="lote" class="aud-field-input form-control form-control-sm" value="${esc(autoFilled?.lote || '')}" placeholder="22514012">
            </div>

            <div class="aud-field-row">
              <label class="aud-field-label"># Transacciones</label>
              <input type="number" name="num_transacciones" class="aud-field-input form-control form-control-sm" value="${esc(autoFilled?.num_transacciones ?? '')}" placeholder="5">
            </div>

            <div class="aud-field-row">
              <label class="aud-field-label">Anulaciones</label>
              <input type="number" name="anulaciones" class="aud-field-input form-control form-control-sm" value="${esc(autoFilled?.anulaciones ?? '')}" placeholder="0">
            </div>
          </div>

          <div class="aud-col-money">
            <div class="aud-group-sep">Montos del informe</div>

            <div class="aud-field-row">
              <label class="aud-field-label">Monto bruto</label>
              <div class="aud-field-money-wrap">
                <input type="number" step="1" name="monto_bruto" class="aud-field-input aud-field-money form-control form-control-sm" value="${esc(autoFilled?.monto_bruto ?? '')}" placeholder="0">
                <span class="aud-field-fmt" id="conc-fmt-bruto">${autoFilled?.monto_bruto ? fmt(autoFilled.monto_bruto) : ''}</span>
              </div>
            </div>

            <div class="aud-field-row">
              <label class="aud-field-label">Comision</label>
              <div class="aud-field-money-wrap">
                <input type="number" step="1" name="comision" class="aud-field-input aud-field-money form-control form-control-sm" value="${esc(autoFilled?.comision ?? '')}" placeholder="0">
                <span class="aud-field-fmt" id="conc-fmt-com">${autoFilled?.comision ? fmt(autoFilled.comision) : ''}</span>
              </div>
            </div>

            <div class="aud-field-row">
              <label class="aud-field-label">IVA comision</label>
              <div class="aud-field-money-wrap">
                <input type="number" step="1" name="iva_comision" class="aud-field-input aud-field-money form-control form-control-sm" value="${esc(autoFilled?.iva_comision ?? '')}" placeholder="0">
                <span class="aud-field-fmt" id="conc-fmt-iva">${autoFilled?.iva_comision ? fmt(autoFilled.iva_comision) : ''}</span>
              </div>
            </div>

            <div class="aud-field-row aud-field-total">
              <label class="aud-field-label">Monto neto</label>
              <div class="aud-field-money-wrap">
                <input type="number" step="1" name="monto_neto" class="aud-field-input aud-field-money form-control form-control-sm" value="${esc(autoFilled?.monto_neto ?? '')}" placeholder="0" required>
                <span class="aud-field-fmt" id="conc-fmt-neto">${autoFilled?.monto_neto ? fmt(autoFilled.monto_neto) : ''}</span>
              </div>
            </div>
          </div>
        </form>

        <div class="aud-form-actions">
          <button class="btn btn-primary" id="aud-btn-conc-save">Guardar conciliacion</button>
        </div>

        <div id="aud-conc-match" style="margin-top:1rem"></div>
      </section>

      <section class="aud-hist-section">
        <div class="aud-section-title">Historial de conciliaciones</div>
        <div id="aud-conc-hist-content">${renderConciliaciones(conciliacionesItems)}</div>
      </section>`;
  }

  function renderConciliaciones(items) {
    if (!items?.length) {
      return `<div class="aud-hist-empty">No hay conciliaciones registradas.</div>`;
    }
    const rows = items.map(r => {
      const zCaja = r.zCaja || r.z_caja;
      const zNum = r.zNumeroCierre || r.z_numero_cierre;
      const vincHtml = zCaja
        ? `<span style="color:#4ade80;font-weight:600">Caja ${esc(zCaja)} #${esc(zNum)}</span>`
        : `<span style="color:#8b949e;font-style:italic">Sin vincular</span>`;
      return `
        <tr>
          <td>${esc(r.fecha || '—')}</td>
          <td>${esc(r.proveedor || '—')}</td>
          <td>${esc(r.terminalId || r.terminal_id || '—')}</td>
          <td>${esc(r.lote || '—')}</td>
          <td style="text-align:center">${r.numTransacciones || r.num_transacciones || 0}</td>
          <td class="aud-td-right">${fmt(r.montoBruto || r.monto_bruto)}</td>
          <td class="aud-td-right">${fmt(r.montoNeto || r.monto_neto)}</td>
          <td>${vincHtml}</td>
        </tr>`;
    }).join('');

    return `
      <table class="aud-hist-table">
        <thead><tr>
          <th>Fecha</th><th>Proveedor</th><th>Terminal</th><th>Lote</th>
          <th>Trans.</th><th class="aud-td-right">Bruto</th><th class="aud-td-right">Neto</th>
          <th>Cierre Z</th>
        </tr></thead>
        <tbody>${rows}</tbody>
      </table>`;
  }

  // ── Conciliacion banco (CB-T4 scaffold) ──────────────────────────────────
  // Modalidad NUEVA del artefacto Auditorias a nivel de CUENTA bancaria (bank_movements),
  // distinta de "Conciliacion externa" (datafono, conciliaciones_externas). Ver #2393.
  // Reusa el ActionArea match-review EXACTO (mismo componente que Compras): el mock sigue
  // el contrato real del componente. Backend real = Argy-A CB-T1 (parser) + CB-T2 (match).
  function renderTabConciliacionBanco() {
    return `
      <section class="aud-upload-section">
        <div class="aud-section-title">Conciliacion bancaria (extracto de cuenta)</div>
        <p style="font-size:0.875rem;color:#8b949e;margin-bottom:1rem">
          Sube el extracto <strong>.xlsx</strong> de la cuenta bancaria. El sistema cruzara los
          movimientos contra ventas por transferencia y egresos por fecha + monto, y abrira una
          <strong>tabla interactiva</strong> para confirmar cada match (o crear el egreso faltante).
        </p>

        <div style="display:flex;flex-wrap:wrap;gap:8px;margin-bottom:14px">
          <button type="button" class="btn btn-ghost btn-sm" data-cb-subtab="upload"
            style="${bancoSubtab === 'upload' ? 'border-color:#1f6feb;color:#79c0ff' : ''}">Subir extracto</button>
          <button type="button" class="btn btn-ghost btn-sm" data-cb-subtab="propuestas"
            style="${bancoSubtab === 'propuestas' ? 'border-color:#1f6feb;color:#79c0ff' : ''}">Propuestas por dia</button>
          ${atribucionBanco.estado.visible ? `<button type="button" class="btn btn-ghost btn-sm" data-cb-subtab="atribuir"
            style="${bancoSubtab === 'atribuir' ? 'border-color:#1f6feb;color:#79c0ff' : ''}">Atribuir movimientos</button>
          <button type="button" class="btn btn-ghost btn-sm" data-cb-subtab="por-negocio"
            style="${bancoSubtab === 'por-negocio' ? 'border-color:#1f6feb;color:#79c0ff' : ''}">Por negocio</button>` : ''}
          <button type="button" class="btn btn-ghost btn-sm" data-cb-subtab="revision"
            style="${bancoSubtab === 'revision' ? 'border-color:#1f6feb;color:#79c0ff' : ''}">Bandeja de revision</button>
          <button type="button" class="btn btn-ghost btn-sm" data-cb-subtab="rolling"
            style="${bancoSubtab === 'rolling' ? 'border-color:#1f6feb;color:#79c0ff' : ''}">Saldo rodante</button>
          <button type="button" class="btn btn-ghost btn-sm" data-cb-subtab="historial"
            style="${bancoSubtab === 'historial' ? 'border-color:#1f6feb;color:#79c0ff' : ''}">Historial</button>
        </div>

        ${bancoSubtab === 'rolling' ? `
          <div style="display:flex;gap:8px;flex-wrap:wrap;align-items:flex-end;margin-bottom:12px;background:#161b22;border:1px solid #30363d;border-radius:8px;padding:10px 12px">
            <label style="display:flex;flex-direction:column;gap:4px;font-size:0.875rem;color:#8b949e">Base (saldo inicial)
              <input type="number" id="cb-roll-base" value="${bancoRolling.base}" step="1000"
                style="width:130px;padding:5px 8px;background:#0d1117;border:1px solid #30363d;border-radius:4px;color:#e1e4e8;font-size:0.875rem">
            </label>
            <label style="display:flex;flex-direction:column;gap:4px;font-size:0.875rem;color:#8b949e">Desde
              <input type="date" id="cb-roll-desde" value="${bancoRolling.desde}"
                style="padding:5px 8px;background:#0d1117;border:1px solid #30363d;border-radius:4px;color:#e1e4e8;font-size:0.875rem">
            </label>
            <label style="display:flex;flex-direction:column;gap:4px;font-size:0.875rem;color:#8b949e">Hasta
              <input type="date" id="cb-roll-hasta" value="${bancoRolling.hasta}"
                style="padding:5px 8px;background:#0d1117;border:1px solid #30363d;border-radius:4px;color:#e1e4e8;font-size:0.875rem">
            </label>
            <button class="btn btn-primary btn-sm" id="cb-roll-calc">Calcular</button>
          </div>
          <div id="cb-rolling-content">${renderBancoRollingContent()}</div>
        ` : bancoSubtab === 'propuestas' ? `
          <div id="cb-prop-content"></div>
        ` : bancoSubtab === 'atribuir' ? `
          <div id="cb-atrib-content"></div>
        ` : bancoSubtab === 'por-negocio' ? `
          <div id="cb-netos-content"></div>
        ` : bancoSubtab === 'revision' ? `
          <div id="cb-rev-content"><p class="empty-state">Cargando movimientos por revisar…</p></div>
        ` : bancoSubtab === 'upload' ? `
          <div class="aud-drop-zone" id="cb-drop-zone">
            ${bancoFile ? `
              <div class="aud-pdf-icon">XLS</div>
              <p class="aud-drop-name">${esc(bancoFile.name)}</p>
              <p class="aud-drop-hint">${(bancoFile.size / 1024).toFixed(0)} KB — listo para procesar</p>
            ` : `
              <div class="aud-drop-icon">+</div>
              <p class="aud-drop-main">Arrastra el extracto .xlsx o haz clic</p>
              <p class="aud-drop-hint">Columnas: Fecha &middot; Descripcion &middot; Referencia &middot; Valor (con signo)</p>
            `}
            <input type="file" id="cb-file-input" accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" style="display:none">
          </div>
          <div class="aud-upload-actions" style="margin-top:.6rem">
            <button class="btn btn-ghost btn-sm" id="cb-btn-browse">Explorar archivos</button>
            <button class="btn btn-primary" id="cb-btn-procesar">Procesar extracto</button>
          </div>
          <div style="margin-top:8px;padding:8px 12px;font-size:0.875rem;color:#8b949e;background:#161b22;border:1px solid #21262d;border-radius:6px">
            El extracto se parsea en el servidor (movimientos con signo) y se cruza contra ventas por
            transferencia y egresos por fecha + monto. <strong>Procesar</strong> abre la tabla de conciliacion.
          </div>
          <div data-cb-status style="margin-top:8px;font-size:0.875rem"></div>
          <div data-cb-days style="margin-top:10px;display:flex;gap:6px;flex-wrap:wrap"></div>
          <div data-cb-actionarea style="margin-top:14px"></div>
        ` : `
          <div id="cb-hist-content">${renderConciliacionBancoHist(bancoHistItems)}</div>
        `}
      </section>`;
  }

  // ── CONCILIA-UI: saldo rodante banco vs sistema con el desfase EXPLICADO ──

  function fmtCOP(n) {
    const v = Number(n || 0);
    return (v < 0 ? '-$' : '$') + new Intl.NumberFormat('es-CO').format(Math.abs(Math.round(v)));
  }

  function renderBancoRollingContent() {
    if (bancoRolling.loading) return '<p class="empty-state">Calculando saldo rodante…</p>';
    if (bancoRolling.error) return `<div style="padding:14px;color:#f85149;background:#161b22;border:1px solid #f85149;border-radius:8px;font-size:0.875rem">${esc(bancoRolling.error)}</div>`;
    const d = bancoRolling.data;
    if (!d) return `<div class="aud-hist-empty">Fija la base (el saldo real de la cuenta al inicio del rango) y presiona <strong>Calcular</strong>.<br>
      <span style="font-size:0.875rem">El desfase = transferencias reportadas que no llegaron al banco (fantasma) + debitos del banco sin egreso registrado en el sistema.</span></div>`;

    const dias = Array.isArray(d.dias) ? d.dias : [];
    const desfase = Number(d.desfaseTotal || 0);
    const okTotal = Math.abs(desfase) < 1;

    return `
      <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(170px,1fr));gap:10px;margin-bottom:12px">
        <div style="background:#161b22;border:1px solid #30363d;border-radius:8px;padding:12px">
          <div style="font-size:0.875rem;color:#8b949e;text-transform:uppercase">Saldo banco final</div>
          <div style="font-size:1.2rem;font-weight:700;color:#79c0ff">${fmtCOP(d.saldoBancoFinal)}</div>
        </div>
        <div style="background:#161b22;border:1px solid #30363d;border-radius:8px;padding:12px">
          <div style="font-size:0.875rem;color:#8b949e;text-transform:uppercase">Saldo sistema final</div>
          <div style="font-size:1.2rem;font-weight:700;color:#c9d1d9">${fmtCOP(d.saldoSistemaFinal)}</div>
        </div>
        <div style="background:${okTotal ? '#0d2818' : '#2a1f08'};border:1px solid ${okTotal ? '#3fb950' : '#d29922'};border-radius:8px;padding:12px;grid-column:span 2;min-width:260px">
          <div style="font-size:0.875rem;color:${okTotal ? '#3fb950' : '#d29922'};text-transform:uppercase">Desfase acumulado</div>
          <div style="font-size:1.2rem;font-weight:700;color:${okTotal ? '#3fb950' : '#d29922'}">${fmtCOP(desfase)}</div>
          <div style="font-size:0.875rem;color:#c9d1d9;margin-top:4px">
            = ${fmtCOP(d.fantasmaTotal)} en transferencias reportadas que <strong>no llegaron</strong> al banco
            + ${fmtCOP(d.egresoNoRegistradoTotal)} en debitos del banco <strong>sin egreso registrado</strong>.
          </div>
        </div>
      </div>
      ${dias.length ? `
      <div style="overflow-x:auto;background:#161b22;border:1px solid #30363d;border-radius:8px">
        <table class="aud-hist-table" style="min-width:720px">
          <thead><tr>
            <th>Dia</th>
            <th class="aud-td-right">Mov. banco</th>
            <th class="aud-td-right">Saldo banco</th>
            <th class="aud-td-right">Saldo sistema</th>
            <th class="aud-td-right">Fantasma</th>
            <th class="aud-td-right">Sin registrar</th>
            <th class="aud-td-right">Desfase dia</th>
            <th class="aud-td-right">Desfase acum.</th>
          </tr></thead>
          <tbody>
            ${dias.map(r => {
              const dd = Number(r.desfaseDia || 0);
              const rowColor = dd !== 0 ? '#d29922' : '#8b949e';
              return `<tr>
                <td>${esc(String(r.fecha || '').slice(0, 10))}</td>
                <td class="aud-td-right">${fmtCOP(r.bankNet)}</td>
                <td class="aud-td-right" style="color:#79c0ff">${fmtCOP(r.saldoBanco)}</td>
                <td class="aud-td-right">${fmtCOP(r.saldoSistema)}</td>
                <td class="aud-td-right" style="color:${Number(r.fantasma) ? '#f85149' : '#484f58'}">${Number(r.fantasma) ? fmtCOP(r.fantasma) : '—'}</td>
                <td class="aud-td-right" style="color:${Number(r.egresoNoRegistrado) ? '#f85149' : '#484f58'}">${Number(r.egresoNoRegistrado) ? fmtCOP(r.egresoNoRegistrado) : '—'}</td>
                <td class="aud-td-right" style="color:${rowColor};font-weight:${dd !== 0 ? 700 : 400}">${dd !== 0 ? fmtCOP(dd) : '—'}</td>
                <td class="aud-td-right" style="font-weight:600;color:${Number(r.desfaseAcumulado) ? '#d29922' : '#3fb950'}">${fmtCOP(r.desfaseAcumulado)}</td>
              </tr>`;
            }).join('')}
          </tbody>
        </table>
      </div>` : '<div class="aud-hist-empty">Sin dias con movimiento en el rango.</div>'}
      <p style="font-size:0.875rem;color:#8b949e;margin-top:8px">
        Base ${fmtCOP(d.base)} del ${esc(d.desde || '')} al ${esc(d.hasta || '')}. Los dias en ambar explican
        exactamente donde nace cada peso del desfase — para resolverlos usa la Bandeja de revision.
      </p>`;
  }

  async function loadBancoRolling() {
    bancoRolling.loading = true;
    bancoRolling.error = null;
    const $c = container.querySelector('#cb-rolling-content');
    if ($c) $c.innerHTML = renderBancoRollingContent();
    const res = await AuditoriaApiClient.rollingBankBalance(businessId, {
      base: bancoRolling.base, desde: bancoRolling.desde, hasta: bancoRolling.hasta
    });
    bancoRolling.loading = false;
    if (!res.ok) {
      bancoRolling.error = String(res.error || 'No se pudo calcular el saldo rodante');
      bancoRolling.data = null;
    } else {
      bancoRolling.data = res.data;
    }
    const $c2 = container.querySelector('#cb-rolling-content');
    if ($c2) $c2.innerHTML = renderBancoRollingContent();
  }

  function renderConciliacionBancoHist(items) {
    if (!items?.length) {
      return `<div class="aud-hist-empty">
        Aun no hay movimientos bancarios cargados. Sube un <strong>.xlsx</strong> en "Subir extracto".
      </div>`;
    }
    const rows = items.map(r => {
      const valor = Number(r.valor ?? r.monto ?? 0);
      const tipo = r.tipo || (valor >= 0 ? 'credito' : 'debito');
      const color = valor >= 0 ? '#4ade80' : '#f85149';
      return `
        <tr>
          <td>${esc(r.fecha || '—')}</td>
          <td>${esc(r.descripcion || '—')}</td>
          <td>${esc(r.referencia || '—')}</td>
          <td style="text-transform:capitalize">${esc(tipo)}</td>
          <td class="aud-td-right" style="color:${color}">${fmt(valor)}</td>
          <td>${esc(r.status || r.estado || '—')}</td>
        </tr>`;
    }).join('');
    return `
      <table class="aud-hist-table">
        <thead><tr>
          <th>Fecha</th><th>Descripcion</th><th>Referencia</th>
          <th>Tipo</th><th class="aud-td-right">Valor</th><th>Estado</th>
        </tr></thead>
        <tbody>${rows}</tbody>
      </table>`;
  }

  // Rango [desde,hasta] a partir del netByDate del upload; fallback: ultimos 120 dias.
  function rangoDeUpload(up) {
    const fechas = (up && Array.isArray(up.netByDate) ? up.netByDate : [])
      .map(n => n.fecha).filter(Boolean).sort();
    if (fechas.length) return { desde: String(fechas[0]).slice(0, 10), hasta: String(fechas[fechas.length - 1]).slice(0, 10) };
    const hoy = new Date();
    const atras = new Date(hoy.getTime() - 120 * 24 * 3600 * 1000);
    return { desde: atras.toISOString().slice(0, 10), hasta: hoy.toISOString().slice(0, 10) };
  }

  function setCbStatus(html, color) {
    const $s = container.querySelector('[data-cb-status]');
    if ($s) $s.innerHTML = html ? `<span style="color:${color || '#8b949e'}">${html}</span>` : '';
  }

  // Sube el extracto real -> concilia el rango -> abre match-review con datos reales.
  async function procesarExtractoBanco(btn) {
    const $aa = container.querySelector('[data-cb-actionarea]');
    if (!$aa) return;
    if (!bancoFile) { setCbStatus('Selecciona primero un archivo .xlsx.', '#d29922'); return; }
    if (btn) { btn.disabled = true; btn.textContent = 'Procesando…'; }
    setCbStatus('Subiendo y parseando el extracto…', '#58a6ff');

    const up = await AuditoriaApiClient.uploadBankMovements({ businessId, file: bancoFile });
    if (!up.ok) {
      setCbStatus(`No se pudo procesar el extracto: ${esc(up.error || 'error')}`, '#f85149');
      if (btn) { btn.disabled = false; btn.textContent = 'Procesar extracto'; }
      return;
    }
    const { desde, hasta } = rangoDeUpload(up.data);
    setCbStatus(`Extracto: ${up.data?.inserted ?? 0} nuevos · ${up.data?.duplicates ?? 0} duplicados. Conciliando ${desde} → ${hasta}…`, '#58a6ff');

    const m = await AuditoriaApiClient.matchBankMovements(businessId, { desde, hasta });
    if (btn) { btn.disabled = false; btn.textContent = 'Procesar extracto'; }
    if (!m.ok) { setCbStatus(`No se pudo conciliar: ${esc(m.error || 'error')}`, '#f85149'); return; }

    const tabla = m.data?.tabla || [];
    if (!tabla.length) {
      setCbStatus('Extracto cargado, pero no hay movimientos para conciliar en el rango.', '#d29922');
      $aa.innerHTML = '';
      renderBancoDays();
      loadConciliacionBancoHist();
      return;
    }
    setCbStatus(`✓ ${tabla.length} movimiento(s) conciliados. Revisa dia a dia y confirma.`, '#3fb950');

    // #2478 item2: guardar el match completo, armar selector de dia, abrir el primer dia.
    bancoMatchData = m.data;
    const dias = diasDeTabla(tabla);
    bancoDay = dias[0] || 'todos';
    renderBancoDays();
    openMatchForDay(bancoDay);
  }

  // #2478 item2: dias distintos (YYYY-MM-DD) presentes en la tabla, ordenados.
  function diasDeTabla(tabla) {
    const set = new Set();
    for (const r of (tabla || [])) {
      const d = String(r.fecha || '').slice(0, 10);
      if (d) set.add(d);
    }
    return Array.from(set).sort();
  }

  function fmtDiaCorto(d) {
    if (d === 'todos') return 'Todos';
    const p = String(d).split('-'); // YYYY-MM-DD
    return p.length === 3 ? `${p[2]}/${p[1]}` : d;
  }

  // Renderiza los chips de dia (Todos + uno por fecha, con conteo).
  function renderBancoDays() {
    const $days = container.querySelector('[data-cb-days]');
    if (!$days) return;
    const tabla = bancoMatchData?.tabla || [];
    if (!tabla.length) { $days.innerHTML = ''; return; }
    const dias = diasDeTabla(tabla);
    const opciones = ['todos', ...dias];
    const countOf = (d) => d === 'todos' ? tabla.length : tabla.filter(r => String(r.fecha || '').slice(0, 10) === d).length;
    $days.innerHTML = opciones.map(d => {
      const on = bancoDay === d;
      return `<button type="button" data-cb-day="${esc(d)}"
        style="padding:4px 10px;border-radius:14px;cursor:pointer;font-size:0.875rem;border:1px solid ${on ? '#1f6feb' : '#30363d'};background:${on ? '#1f6feb22' : '#161b22'};color:${on ? '#79c0ff' : '#c9d1d9'}">
        ${esc(fmtDiaCorto(d))} <span style="color:#8b949e">(${countOf(d)})</span></button>`;
    }).join('');
    $days.querySelectorAll('[data-cb-day]').forEach(b => {
      b.addEventListener('click', () => { bancoDay = b.dataset.cbDay; renderBancoDays(); openMatchForDay(bancoDay); });
    });
  }

  // Abre match-review con SOLO los movimientos del dia seleccionado (o todos).
  // BAN-T3: el Confirmar del match-review guarda por la API (ver application/conciliacion/cruceBanco.js).
  const confirmarCruceBanco = confirmadorDeCruce(AuditoriaApiClient, () => businessId);

  function openMatchForDay(day) {
    const $aa = container.querySelector('[data-cb-actionarea]');
    if (!$aa || !bancoMatchData) return;
    const full = bancoMatchData.tabla || [];
    const tabla = (day && day !== 'todos')
      ? full.filter(r => String(r.fecha || '').slice(0, 10) === day)
      : full;
    // #2531 f2: el dropdown de candidatos solo debe ofrecer pagos del MISMO dia del movimiento.
    // El candidato ya trae su fecha en `presentacion` (ver _cbRefToCandidato). Filtramos client-side
    // sin tocar match-review (compartido con Compras). Conservamos el candidato ya elegido y los
    // sin fecha (defensivo) para no romper un MATCH existente ni ocultar refs legitimas.
    const mismodia = full.map(r => {
      const rf = String(r.fecha || '').slice(0, 10);
      if (!rf || !Array.isArray(r.candidatos) || r.candidatos.length <= 1) return r;
      const mismos = r.candidatos.filter(c => {
        const cf = String(c.presentacion || '').slice(0, 10);
        return !cf || cf === rf || c.insumo_id === r.insumo_id_elegido;
      });
      return mismos.length === r.candidatos.length ? r : { ...r, candidatos: mismos };
    });
    const tablaDia = (day && day !== 'todos')
      ? mismodia.filter(r => String(r.fecha || '').slice(0, 10) === day)
      : mismodia;
    // Resumen recalculado para el subconjunto del dia.
    const resumen = {
      items_total: tabla.length,
      match_sugeridos: tabla.filter(t => t.accion === 'MATCH').length,
      crear_sugeridos: tabla.filter(t => t.accion === 'CREATE').length,
      review: tabla.filter(t => t.accion === 'REVIEW').length
    };
    bancoActionArea = createActionArea($aa);
    bancoActionArea.showMode('match-review', {
      businessId,
      labels: CB_MATCH_LABELS, // #2478 item1: textos de conciliacion, no compras
      pregunta_al_usuario: day && day !== 'todos'
        ? `Movimientos del ${fmtDiaCorto(day)}: confirma como cruza cada uno.`
        : 'Confirma como cruza cada movimiento del banco.',
      // BAN-T3 (ronda #20837): el Confirmar GUARDA por la API. Antes pegaba a n8n
      // /webhook/conciliacion-banco/apply, que nunca existio (medido 1-oct: 404 "not registered"), asi que confirmar
      // no dejaba nada. Ahora cada movimiento cruzado (MATCH) queda 'matched' con POST bank-movements/status (CB-T4).
      // Crear egresos de los sin-match sigue en la Bandeja de revision (createEgresosFromConciliacion).
      confirmar: confirmarCruceBanco,
      tabla: tablaDia, // #2531 f2: candidatos ya filtrados al mismo dia
      resumen,
      onConfirmed: ({ ok: okc, aprobaciones, response }) => {
        console.log('[ConciliacionBanco] match confirmado:', { ok: okc, aprobaciones, response });
        loadConciliacionBancoHist();
      }
    });
  }

  async function loadConciliacionBancoHist() {
    let res = null;
    try { res = await AuditoriaApiClient.listBankMovements(businessId); } catch { /* no-op */ }
    bancoHistItems = (res && res.ok && Array.isArray(res.items)) ? res.items : [];
    const $c = container.querySelector('#cb-hist-content');
    if ($c) $c.innerHTML = renderConciliacionBancoHist(bancoHistItems);
  }

  // ── #2495 frente1: Bandeja de revision (movimientos requiere-revision) ─────
  async function loadBancoRevision(flash) {
    const $c = container.querySelector('#cb-rev-content');
    if ($c && !flash) $c.innerHTML = '<p class="empty-state">Cargando movimientos por revisar…</p>';
    const hoy = new Date();
    const desde = new Date(hoy.getTime() - 365 * 24 * 3600 * 1000).toISOString().slice(0, 10);
    const hasta = hoy.toISOString().slice(0, 10);
    const m = await AuditoriaApiClient.matchBankMovements(businessId, { desde, hasta });
    const tabla = (m.ok && m.data && Array.isArray(m.data.tabla)) ? m.data.tabla : [];
    bancoRevAll = tabla;                              // #2531 f4
    bancoRevResumen = (m.ok && m.data) ? m.data.resumen : null;  // #2531 f4
    // Requieren atencion humana: REVIEW o requiere-revision o sin-match.
    bancoRevItems = tabla.filter(r =>
      r.accion === 'REVIEW' || r.requiereRevision === true ||
      r.estado === 'requiere-revision' || r.estado === 'sin-match');
    if ($c) $c.innerHTML = renderBancoRevisionContent(bancoRevItems, flash);
    bindBancoRevisionEvents();
  }

  // #2531 f4: desglose SIEMPRE consistente. Cada movimiento cae en exactamente un bucket
  // y los buckets suman al total exacto (elimina el "292 vs 210+23 no cuadra").
  function bancoDesglose(all) {
    const esAjuste = (r) => String(r.categoria || '').toLowerCase() === 'ajuste';
    const ajustes = all.filter(esAjuste).length;
    const noAj = all.filter(r => !esAjuste(r));
    const cruzados = noAj.filter(r => r.accion === 'MATCH').length;
    const crear = noAj.filter(r => r.accion === 'CREATE').length;
    const porRevisar = noAj.length - cruzados - crear; // resto = REVIEW/sin-match
    const total = all.length;
    const itemsTotal = Number(bancoRevResumen?.items_total ?? total);
    const sinLlegar = Number(bancoRevResumen?.reportadas_no_llegaron ?? 0);
    return { total, cruzados, crear, porRevisar, ajustes, itemsTotal, sinLlegar };
  }

  function renderBancoBreakdown(all) {
    if (!all.length) return '';
    const d = bancoDesglose(all);
    const chip = (label, n, color) => `<span style="padding:2px 9px;border-radius:11px;font-size:0.875rem;background:${color}22;color:${color};white-space:nowrap">${label} <strong>${n}</strong></span>`;
    // Nota si el total del backend no coincide con las filas conciliadas (movimientos reportados que no llegaron al extracto).
    const descuadre = d.itemsTotal !== d.total;
    const notaSinLlegar = d.sinLlegar > 0
      ? `<div style="font-size:0.875rem;color:#8b949e;margin-top:4px">+ ${d.sinLlegar} reportado(s) por el sistema que no aparecieron en el extracto (no cuentan en el total conciliable).</div>` : '';
    const notaDesc = (descuadre && !d.sinLlegar)
      ? `<div style="font-size:0.875rem;color:#d29922;margin-top:4px">Backend reporta ${d.itemsTotal} movimiento(s); se conciliaron ${d.total}. Diferencia: ${d.itemsTotal - d.total}.</div>` : '';
    return `
      <div style="border:1px solid #21262d;background:#0d1117;border-radius:8px;padding:10px 12px;margin-bottom:10px">
        <div style="display:flex;align-items:center;gap:8px;flex-wrap:wrap;font-size:0.875rem">
          <span style="color:#8b949e">Total conciliable</span>
          <strong style="font-size:.9rem;color:#f0f6fc">${d.total}</strong>
          <span style="color:#6e7681">=</span>
          ${chip('Cruzados', d.cruzados, '#3fb950')}
          ${chip('Por revisar', d.porRevisar, '#d29922')}
          ${chip('Egresos a crear', d.crear, '#58a6ff')}
          ${chip('Ajustes', d.ajustes, '#d29922')}
        </div>
        ${notaSinLlegar}${notaDesc}
        <div style="font-size:0.875rem;color:#6e7681;margin-top:4px">El split confirmado / pendiente / en-revision se activa con el estado persistido (F1, backend).</div>
      </div>`;
  }

  function renderBancoRevisionContent(items, flash) {
    const flashHtml = flash ? `<div style="margin-bottom:8px;font-size:0.875rem;color:${flash.color || '#3fb950'}">${esc(flash.msg)}</div>` : '';
    const breakdown = renderBancoBreakdown(bancoRevAll); // #2531 f4: desglose SIEMPRE visible
    if (!items.length) {
      return `${flashHtml}${breakdown}<div class="aud-hist-empty">No hay movimientos pendientes de revision. &#127881;</div>`;
    }
    const esAjuste = (r) => String(r.categoria || '').toLowerCase() === 'ajuste';
    const ajustes = items.filter(esAjuste);
    const otros = items.filter(r => !esAjuste(r));
    const sumAjustes = ajustes.reduce((s, r) => s + Math.abs(Number(r.monto) || 0), 0);
    return `
      ${flashHtml}
      ${breakdown}
      <div style="font-size:0.875rem;color:#8b949e;margin-bottom:8px">
        ${items.length} movimiento(s) por revisar${ajustes.length ? ` &middot; ${ajustes.length} ajuste(s) agrupados` : ''}. Reclasifica cada uno: crear egreso, confirmar o ignorar.
      </div>
      ${ajustes.length ? renderAjustesGroup(ajustes, sumAjustes) : ''}
      ${otros.map(r => renderRevRow(r)).join('')}`;
  }

  // #2495 frente1(b): ajustes bancarios (intereses/comisiones) agrupados en 1 linea expandible.
  function renderAjustesGroup(ajustes, sum) {
    const ids = ajustes.map(a => a.item_id).join(',');
    return `
      <div style="border:1px solid #d2992244;background:#d2992211;border-radius:8px;margin-bottom:8px">
        <div style="display:flex;align-items:center;gap:10px;padding:10px 12px;cursor:pointer" data-cb-ajustes-toggle>
          <span style="font-size:1.1rem">&#129689;</span>
          <div style="flex:1"><strong style="color:#d29922">${ajustes.length} ajustes bancarios</strong>
            <span style="color:#8b949e;font-size:0.875rem"> &middot; intereses/comisiones &middot; total ${fmt(sum)}</span></div>
          <button class="btn btn-ghost btn-sm" data-cb-ajustes-ignore="${esc(ids)}" style="font-size:0.875rem">Ignorar todos</button>
          <span data-cb-ajustes-caret style="color:#8b949e">&#9662;</span>
        </div>
        <div data-cb-ajustes-list style="display:none;padding:0 12px 10px">
          ${ajustes.map(a => `
            <div style="display:flex;justify-content:space-between;gap:8px;font-size:0.875rem;color:#c9d1d9;padding:4px 0;border-top:1px solid #21262d">
              <span>${esc(a.fecha || '')} &middot; ${esc(a.descripcion || '')}</span>
              <span style="color:#f85149">${fmt(a.monto)}</span></div>`).join('')}
        </div>
      </div>`;
  }

  function renderRevRow(r) {
    const esDebito = String(r.tipo || '').toLowerCase() === 'debito' || Number(r.monto) < 0;
    const color = Number(r.monto) >= 0 ? '#4ade80' : '#f85149';
    return `
      <div class="pdt-card" style="padding:10px 12px;margin-bottom:6px;border:1px solid #21262d;border-radius:8px;background:#161b22">
        <div style="display:flex;align-items:center;gap:10px;flex-wrap:wrap">
          <div style="flex:1;min-width:180px">
            <div style="color:#c9d1d9;font-size:0.875rem">${esc(r.descripcion || '(sin descripcion)')}</div>
            <div style="font-size:0.875rem;color:#8b949e">${esc(r.fecha || '')} &middot; ${esc(r.tipo || '')}${r.categoria ? ` &middot; ${esc(r.categoria)}` : ''}</div>
          </div>
          <span style="font-family:monospace;font-weight:600;color:${color}">${fmt(r.monto)}</span>
          ${r.es_especial ? `<span title="Manejo exclusivo en panel — no baja a Desktop" style="background:#e3b34122;color:#e3b341;border-radius:8px;padding:1px 7px;font-size:0.875rem;font-weight:700;white-space:nowrap">⭐ ESPECIAL</span>` : ''}
          <label style="display:inline-flex;align-items:center;gap:6px;font-size:0.875rem;color:${r.es_especial ? '#e3b341' : '#8b949e'};cursor:pointer;white-space:nowrap"
                 title="Movimiento de manejo exclusivo en panel (egreso personal / ingreso ajeno). NO se sincroniza a Desktop.">
            <input type="checkbox" data-cb-rev-especial="${esc(r.item_id)}" ${r.es_especial ? 'checked' : ''} style="cursor:pointer">
            Especial &mdash; solo panel
          </label>
          <div style="display:flex;gap:6px">
            ${esDebito
              ? `<button class="btn btn-primary btn-sm" data-cb-rev-egreso="${esc(r.item_id)}" style="font-size:0.875rem${r.es_especial ? ';background:#e3b341;border-color:#e3b341;color:#0d1117' : ''}" title="${r.es_especial ? 'Se creara como SOLO PANEL: no bajara al programa del negocio' : 'Se creara normal y bajara al programa del negocio. Si debe quedarse solo en el panel, marca la casilla ANTES.'}">Crear egreso${r.es_especial ? ' &middot; solo panel' : ''}</button>`
              : `<button class="btn btn-ghost btn-sm" data-cb-rev-confirm="${esc(r.item_id)}" style="font-size:0.875rem" title="Confirmar como venta/ingreso">Confirmar</button>`}
            <button class="btn btn-ghost btn-sm" data-cb-rev-ignore="${esc(r.item_id)}" style="font-size:0.875rem" title="Ajuste, no genera egreso">Ignorar</button>
          </div>
        </div>
        <!-- CB-F7-UI-2 (bitacora #9349): el egreso HEREDA es_especial del movimiento EN EL
             MOMENTO DEL INSERT (subconsulta dentro del INSERT). Marcarlo DESPUES de crear no
             sirve: el flip de confirmed ya encolo al Desktop y el INSERT es idempotente
             (ON CONFLICT DO NOTHING), asi que el egreso ya bajo. Por eso la casilla subio
             ANTES del boton y aqui queda el aviso, solo cuando todavia se puede evitar. -->
        ${esDebito && !r.es_especial
          ? `<div style="margin-top:6px;font-size:0.875rem;color:#8b949e">Si este gasto debe quedarse <strong style="color:#e3b341">solo en el panel</strong>, marca la casilla <strong>antes</strong> de crear el egreso: despues de creado ya no lo protege.</div>`
          : ''}
      </div>`;
  }

  async function resolverMovimientos(movimientos, okMsg) {
    const res = await AuditoriaApiClient.confirmBankStatus(businessId, movimientos);
    if (!res.ok) { loadBancoRevision({ msg: `Error: ${res.error || 'no se pudo actualizar'}`, color: '#f85149' }); return; }
    loadBancoRevision({ msg: `${okMsg} (${res.data?.actualizados ?? movimientos.length})`, color: '#3fb950' });
  }

  async function crearEgresoDeMovimiento(id) {
    const r = bancoRevItems.find(x => String(x.item_id) === String(id));
    if (!r) return;
    const res = await AuditoriaApiClient.createEgresosFromConciliacion(businessId, [{
      bankMovementId: id, monto: Math.abs(Number(r.monto) || 0), fecha: r.fecha, concepto: r.descripcion, metodo: 'Externo'
    }]);
    if (!res.ok) { loadBancoRevision({ msg: `Error: ${res.error || 'no se pudo crear el egreso'}`, color: '#f85149' }); return; }
    const cr = res.data?.creados ?? 0;
    loadBancoRevision({ msg: cr > 0 ? '✓ Egreso creado desde el movimiento' : 'El egreso ya existia (idempotente)', color: '#3fb950' });
  }

  function bindBancoRevisionEvents() {
    const $c = container.querySelector('#cb-rev-content');
    if (!$c) return;
    const $tog = $c.querySelector('[data-cb-ajustes-toggle]');
    if ($tog) $tog.addEventListener('click', (e) => {
      if (e.target.closest('[data-cb-ajustes-ignore]')) return; // el boton no togglea
      const $list = $c.querySelector('[data-cb-ajustes-list]');
      const $caret = $c.querySelector('[data-cb-ajustes-caret]');
      if ($list) {
        const show = $list.style.display === 'none';
        $list.style.display = show ? 'block' : 'none';
        if ($caret) $caret.innerHTML = show ? '&#9652;' : '&#9662;';
      }
    });
    const $bulk = $c.querySelector('[data-cb-ajustes-ignore]');
    if ($bulk) $bulk.addEventListener('click', () => {
      const ids = String($bulk.dataset.cbAjustesIgnore || '').split(',').filter(Boolean);
      resolverMovimientos(ids.map(id => ({ bankMovementId: id, status: 'ignored' })), 'Ajustes ignorados');
    });
    $c.querySelectorAll('[data-cb-rev-ignore]').forEach(b => b.addEventListener('click', () =>
      resolverMovimientos([{ bankMovementId: b.dataset.cbRevIgnore, status: 'ignored' }], 'Movimiento ignorado')));
    $c.querySelectorAll('[data-cb-rev-confirm]').forEach(b => b.addEventListener('click', () =>
      resolverMovimientos([{ bankMovementId: b.dataset.cbRevConfirm, status: 'matched' }], 'Movimiento confirmado')));
    $c.querySelectorAll('[data-cb-rev-egreso]').forEach(b => b.addEventListener('click', () =>
      crearEgresoDeMovimiento(b.dataset.cbRevEgreso)));
    // CB-F7-UI: toggle ESPECIAL (solo panel). PATCH backend -> no baja a Desktop.
    $c.querySelectorAll('[data-cb-rev-especial]').forEach(chk => chk.addEventListener('change', () =>
      marcarEspecial(chk.dataset.cbRevEspecial, chk.checked)));
  }

  async function marcarEspecial(id, esEspecial) {
    const res = await AuditoriaApiClient.patchBankMovement(businessId, id, { esEspecial });
    if (!res.ok) { loadBancoRevision({ msg: `Error: ${res.error || 'no se pudo marcar especial'}`, color: '#f85149' }); return; }
    loadBancoRevision({
      msg: esEspecial ? '⭐ Marcado como especial (no baja a Desktop)' : 'Especial desmarcado',
      color: '#e3b341'
    });
  }

  // ── Event bindings ───────────────────────────────────────────────────────
  function bindEvents() {
    // Tabs
    container.querySelectorAll('.aud-tab').forEach(tab => {
      tab.addEventListener('click', () => {
        activeTab = tab.dataset.tab;
        render();
        if (activeTab === 'balance' && !balanceItems.length) loadBalance();
        if (activeTab === 'colaboradores' && !metricasItems.length) loadMetricas();
        if (activeTab === 'conciliacion') loadConciliaciones();
      });
    });

    // Conciliacion banco (CB-T4): subtabs + dropzone .xlsx + procesar -> match-review
    if (activeTab === 'conciliacion-banco') {
      container.querySelectorAll('[data-cb-subtab]').forEach(b => {
        b.addEventListener('click', () => {
          bancoSubtab = b.dataset.cbSubtab;
          render();
          if (bancoSubtab === 'historial' && !bancoHistItems.length) loadConciliacionBancoHist();
          if (bancoSubtab === 'revision') loadBancoRevision();
        });
      });
      if (bancoSubtab === 'propuestas') bandejaPropuestas.montar(container.querySelector('#cb-prop-content'));
      if (bancoSubtab === 'atribuir') atribucionBanco.montar(container.querySelector('#cb-atrib-content'));
      if (bancoSubtab === 'por-negocio') netosPorNegocio.montar(container.querySelector('#cb-netos-content'));
      // Se pregunta una vez; si la cuenta es multi-negocio, se repinta para que aparezca la subpestana.
      if (!atribucionBanco.estado.comprobado) {
        atribucionBanco.comprobar().then((visible) => { if (visible && activeTab === 'conciliacion-banco') render(); });
      }
      // Cargar la bandeja al (re)entrar al subtab si aun muestra el placeholder.
      if (bancoSubtab === 'revision') {
        const $rc = container.querySelector('#cb-rev-content');
        if ($rc && /Cargando/.test($rc.textContent)) loadBancoRevision();
      }
      // CONCILIA-UI: binds del saldo rodante (inputs de params + calcular).
      if (bancoSubtab === 'rolling') {
        container.querySelector('#cb-roll-base')?.addEventListener('change', e => {
          bancoRolling.base = Number(e.target.value) || 0;
        });
        container.querySelector('#cb-roll-desde')?.addEventListener('change', e => {
          bancoRolling.desde = e.target.value;
        });
        container.querySelector('#cb-roll-hasta')?.addEventListener('change', e => {
          bancoRolling.hasta = e.target.value;
        });
        container.querySelector('#cb-roll-calc')?.addEventListener('click', loadBancoRolling);
      }
      if (bancoSubtab === 'upload') {
        const $drop = container.querySelector('#cb-drop-zone');
        const $file = container.querySelector('#cb-file-input');
        const $browse = container.querySelector('#cb-btn-browse');
        const $proc = container.querySelector('#cb-btn-procesar');
        const pick = () => $file && $file.click();
        if ($browse) $browse.addEventListener('click', pick);
        if ($drop) {
          $drop.addEventListener('click', pick);
          $drop.addEventListener('dragover', e => { e.preventDefault(); $drop.classList.add('aud-drop-loading'); });
          $drop.addEventListener('dragleave', () => $drop.classList.remove('aud-drop-loading'));
          $drop.addEventListener('drop', e => {
            e.preventDefault(); $drop.classList.remove('aud-drop-loading');
            const f = e.dataTransfer?.files?.[0];
            if (f) { bancoFile = f; render(); }
          });
        }
        if ($file) $file.addEventListener('change', e => {
          const f = e.target.files?.[0];
          if (f) { bancoFile = f; render(); }
        });
        if ($proc) $proc.addEventListener('click', () => procesarExtractoBanco($proc));
      }
    }

    // Si el tab balance ya esta renderizado con datos (re-render por cambio de tab),
    // re-bind los handlers de expansion porque el HTML se reemplazo.
    if (activeTab === 'balance' && balanceItems.length) bindBalanceExpandHandlers();

    // Conciliacion externa: formato moneda en tiempo real + matching con cierre Z
    container.querySelectorAll('#aud-conc-form .aud-field-money').forEach(inp => {
      inp.addEventListener('input', (e) => {
        const wrap = e.target.closest('.aud-field-money-wrap');
        const fmtEl = wrap?.querySelector('.aud-field-fmt');
        if (fmtEl) fmtEl.textContent = e.target.value ? fmt(e.target.value) : '';
      });
    });

    // Al cambiar fecha, buscar matching con cierre Z del mismo dia
    const concFecha = container.querySelector('#aud-conc-form [name="fecha"]');
    if (concFecha) {
      concFecha.addEventListener('change', () => matchConciliacionConZ());
    }
    const concNeto = container.querySelector('#aud-conc-form [name="monto_neto"]');
    if (concNeto) {
      concNeto.addEventListener('input', () => matchConciliacionConZ());
    }

    container.querySelector('#aud-btn-conc-save')?.addEventListener('click', handleSaveConciliacion);

    // Drop zone
    const dropZone  = container.querySelector('#aud-drop-zone');
    const fileInput = container.querySelector('#aud-file-input');

    if (dropZone && phase === 'upload') {
      dropZone.addEventListener('click', () => fileInput?.click());
      dropZone.addEventListener('dragover', e => { e.preventDefault(); dropZone.classList.add('aud-drop-over'); });
      dropZone.addEventListener('dragleave', () => dropZone.classList.remove('aud-drop-over'));
      dropZone.addEventListener('drop', e => {
        e.preventDefault(); dropZone.classList.remove('aud-drop-over');
        const file = e.dataTransfer?.files?.[0];
        if (file) handleFileSelected(file);
      });
    }

    fileInput?.addEventListener('change', e => {
      const file = e.target.files?.[0];
      if (file) handleFileSelected(file);
    });

    container.querySelector('#aud-btn-browse')?.addEventListener('click', e => {
      e.stopPropagation(); fileInput?.click();
    });
    container.querySelector('#aud-btn-extract')?.addEventListener('click', handleExtract);
    container.querySelector('#aud-btn-discard')?.addEventListener('click', handleDiscard);
    container.querySelector('#aud-btn-save')?.addEventListener('click', handleSave);
    container.querySelector('#aud-btn-nueva')?.addEventListener('click', handleDiscard);

    // Revalidacion en tiempo real al editar cualquier campo del formulario de cierre Z
    const confirmForm = container.querySelector('#aud-confirm-form');
    if (confirmForm && extractedTipo === 'cierre_z') {
      confirmForm.querySelectorAll('input[name]').forEach(inp => {
        inp.addEventListener('input', (e) => {
          const k = e.target.name;
          const v = e.target.type === 'number' ? (e.target.value !== '' ? parseFloat(e.target.value) : null) : (e.target.value || null);
          extractedData[k] = v;
          // Actualizar formato de moneda al lado
          if (e.target.classList.contains('aud-field-money')) {
            const wrap = e.target.closest('.aud-field-money-wrap');
            const fmtEl = wrap?.querySelector('.aud-field-fmt');
            if (fmtEl) fmtEl.textContent = v != null ? fmt(v) : '';
          }
          // Recomputar validaciones
          extractedData.validacion = recomputeValidaciones(extractedData);
          updateValidationDisplay();
        });
      });
    }

    // Historial clickeable: al click, re-cargar el cierre para visualizar
    container.querySelectorAll('.aud-hist-row-clickable').forEach(row => {
      row.addEventListener('click', (e) => {
        // Ignorar click si fue en el boton de eliminar
        if (e.target.closest('.aud-btn-delete')) return;
        const idx = parseInt(row.dataset.idx, 10);
        const item = historialItems[idx];
        if (item && item.id) {
          loadCierreById(item.id);
        }
      });
    });

    // Boton X: eliminar cierre
    container.querySelectorAll('.aud-btn-delete').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        e.stopPropagation();
        const id = btn.dataset.id;
        const idx = parseInt(btn.dataset.idx, 10);
        const item = historialItems[idx];
        if (!id) {
          avisar('Este cierre no tiene un ID valido.', { tono: 'error' });
          return;
        }
        const descripcion = item
          ? `cierre #${item.numeroCierre ?? item.numero_cierre ?? '—'} de ${item.fechaCierre ?? item.fecha_cierre ?? '—'}`
          : 'este cierre';
        if (!await confirmar({ titulo: `Eliminar ${descripcion}`, texto: 'Esta accion no se puede deshacer.', aceptar: 'Eliminar cierre', peligro: true })) return;

        const res = await AuditoriaApiClient.delete(id);
        if (!res.ok) {
          avisar(`No se pudo eliminar: ${res.error?.message || res.error}`, { tono: 'error' });
          return;
        }
        await loadHistorial();
      });
    });
    container.querySelector('#aud-btn-balance')?.addEventListener('click', () => {
      balanceDesde = container.querySelector('#aud-bal-desde')?.value || '';
      balanceHasta = container.querySelector('#aud-bal-hasta')?.value || '';
      loadBalance();
    });
  }

  // ── Handlers ─────────────────────────────────────────────────────────────
  async function handleFileSelected(file) {
    if (previewUrl) URL.revokeObjectURL(previewUrl);

    let finalFile = file;
    if (file.size > AUD_MAX_UPLOAD_BYTES) {
      const sizeMB = (file.size / 1024 / 1024).toFixed(2);
      const isImage = (file.type || '').startsWith('image/');
      if (!isImage) {
        avisar(`El archivo pesa ${sizeMB} MB y el limite es ${AUD_MAX_UPLOAD_MB} MB. Reduce el PDF antes de subirlo y vuelve a intentar.`, { tono: 'error', ms: 9000 });
        return;
      }
      try {
        finalFile = await compressImageToFit(file, AUD_MAX_UPLOAD_BYTES);
        const newSizeMB = (finalFile.size / 1024 / 1024).toFixed(2);
        console.info(`[Auditoria] Imagen comprimida: ${sizeMB}MB -> ${newSizeMB}MB`);
      } catch (err) {
        console.error('[Auditoria] Compresion fallida:', err);
        avisar(`La imagen pesa ${sizeMB} MB y no se pudo bajar de ${AUD_MAX_UPLOAD_MB} MB. Toma la foto con menos resolucion o reducela e intenta de nuevo.`, { tono: 'error', ms: 9000 });
        return;
      }
    }

    uploadedFile = finalFile;
    previewUrl   = filePreviewUrl(finalFile);
    phase        = 'upload';
    render();
  }

  async function handleExtract() {
    if (!uploadedFile || !businessId) return;
    phase = 'extracting';
    render();

    try {
      const result = await AuditoriaApiClient.extract({
        businessId, businessName, file: uploadedFile,
      });

      if (!result.ok) {
        avisar(`No se pudieron leer los datos: ${result.error?.message || result.error}`, { tono: 'error' });
        phase = 'upload'; render(); return;
      }

      // V2.3: el resultado incluye tipo_documento y confianza
      const payload = result.data ?? {};
      const tipoDetectado = payload.tipo;
      const confianza = payload.confianza || 0;
      const tiposValidos = ['cierre_z', 'conciliacion_externa'];
      const deteccionValida = tiposValidos.includes(tipoDetectado) && confianza >= 30;

      // Fix UX defensive (2026-04-24): si el workflow n8n no devolvio tipo valido o
      // confianza <30%, preguntar al usuario en vez de asumir cierre_z. Antes lo mapeaba
      // automatico a cierre_z con confianza 0, generando formularios vacios confusos.
      if (!deteccionValida) {
        console.warn('[Auditoria] Deteccion incierta:', { tipoDetectado, confianza, payload });
        const eleccion = await pedirTexto({
          titulo: 'Que documento es',
          texto: `No pudimos saber solos que documento es (confianza ${confianza}%).`,
          etiqueta: 'Tipo de documento',
          valor: 'cierre_z',
          opciones: [
            { valor: 'cierre_z', texto: 'Comprobante Z (cierre de caja)' },
            { valor: 'conciliacion_externa', texto: 'Conciliacion externa (CCO / Redeban)' }
          ]
        });
        if (eleccion === null) { phase = 'upload'; render(); return; }
        extractedTipo = eleccion === 'conciliacion_externa' ? 'conciliacion_externa' : 'cierre_z';
        extractedConfianza = 0; // explicito: eleccion manual del usuario
      } else {
        extractedTipo = tipoDetectado;
        extractedConfianza = confianza;
      }
      extractedData = payload.data ?? payload;

      // Si el sistema detecto conciliacion externa, cambiamos al tab correspondiente
      if (extractedTipo === 'conciliacion_externa') {
        activeTab = 'conciliacion';
      } else {
        activeTab = 'comprobantes';
      }

      phase = 'review';
      render();
    } catch (err) {
      console.error('Extraction error:', err);
      avisar('Algo fallo al leer el documento. Intenta de nuevo.', { tono: 'error' });
      phase = 'upload'; render();
    }
  }

  async function handleSave(force = false) {
    if (!extractedData || !businessId) return;
    phase = 'saving'; render();

    const form = container.querySelector('#aud-confirm-form');
    if (form) {
      form.querySelectorAll('input[name]').forEach(inp => {
        const k = inp.name;
        const v = inp.type === 'number' ? (inp.value !== '' ? parseFloat(inp.value) : null) : inp.value || null;
        extractedData[k] = v;
      });
    }

    // Recomputar validacion final antes de guardar
    extractedData.validacion = recomputeValidaciones(extractedData);

    // Mapear snake_case -> camelCase para el API DTO
    const apiData = mapCierreZForApi(extractedData);
    apiData.validacion = extractedData.validacion;
    apiData.rawJson = extractedData;

    const result = await AuditoriaApiClient.save(businessId, apiData, force);
    if (!result.ok) {
      // Detectar duplicado (HTTP 409 devuelve error con .error === 'duplicado')
      const errMsg = result.error?.message || result.error?.error || result.error || '';
      const isDuplicate = String(errMsg).toLowerCase().includes('duplicado') ||
                          result.error?.error === 'duplicado' ||
                          result.error?.code === 409 ||
                          result.code === 409;

      if (isDuplicate && !force) {
        const guardarIgual = await confirmar({
          titulo: 'Ese cierre ya existe',
          texto: `Ya hay un cierre #${extractedData.numero_cierre} de la caja ${extractedData.caja} el ${extractedData.fecha}.\nPuede ser otro cierre del mismo turno.`,
          aceptar: 'Guardar de todos modos'
        });
        if (guardarIgual) {
          // Reintentar con force=true
          return handleSave(true);
        }
        phase = 'review'; render(); return;
      }

      avisar(`No se pudo guardar: ${result.error?.message || errMsg}`, { tono: 'error' });
      phase = 'review'; render(); return;
    }

    phase = 'saved';
    await loadHistorial();
    render();
  }

  function handleDiscard() {
    extractedData = null; extractedTipo = null; extractedConfianza = 0;
    uploadedFile = null;
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    previewUrl = null; phase = 'upload';
    activeTab = 'comprobantes';
    render();
  }

  // ── Data loaders ────────────────────────────────────────────────────────
  async function loadHistorial() {
    if (!businessId) return;
    const res = await AuditoriaApiClient.list(businessId, 1, 50);
    if (res.ok) {
      const raw = res.data?.data?.items ?? res.data?.items ?? res.data ?? [];
      historialItems = Array.isArray(raw) ? raw : [];
    }
    const el = container.querySelector('#aud-hist-content');
    if (el) el.innerHTML = renderHistorial(historialItems);
  }

  async function loadBalance() {
    if (!businessId) return;
    const res = await AuditoriaApiClient.balance(businessId, balanceDesde, balanceHasta);
    if (res.ok) {
      balanceItems = Array.isArray(res.data?.data) ? res.data.data : (Array.isArray(res.data) ? res.data : []);
    }
    // Cargar Z y conciliaciones para que las subfilas expandibles tengan datos.
    if (!historialItems.length) await loadHistorial();
    if (!conciliacionesItems.length) await loadConciliaciones();
    refreshBalanceContent();
  }

  function refreshBalanceContent() {
    const el = container.querySelector('#aud-balance-content');
    if (el) el.innerHTML = renderBalance(balanceItems);
    bindBalanceExpandHandlers();
  }

  function bindBalanceExpandHandlers() {
    container.querySelectorAll('.aud-bal-expand').forEach(btn => {
      btn.addEventListener('click', () => {
        const fecha = btn.dataset.fecha;
        if (!fecha) return;
        if (expandedBalanceDates.has(fecha)) expandedBalanceDates.delete(fecha);
        else expandedBalanceDates.add(fecha);
        refreshBalanceContent();
      });
    });
  }

  function renderBalance(items) {
    if (!items?.length) return `<div class="aud-hist-empty">No hay datos de balance para este periodo.</div>`;

    let totEfectivo = 0, totTarjetas = 0, totQr = 0, totSiste = 0, totInter = 0, totAddi = 0, totVenta = 0;
    const rows = items.map(r => {
      totEfectivo += parseFloat(r.totalEfectivo) || 0;
      totTarjetas += parseFloat(r.totalTarjetas) || 0;
      totQr += parseFloat(r.totalQr) || 0;
      totSiste += parseFloat(r.totalSistecredito) || 0;
      totInter += parseFloat(r.totalIntercredito) || 0;
      totAddi += parseFloat(r.totalAddi) || 0;
      totVenta += parseFloat(r.ventaNetaDia) || 0;

      const fecha = r.fecha ?? '';
      const numCierres = r.numCierres || 0;
      const expandable = numCierres > 0;
      const isExpanded = expandedBalanceDates.has(fecha);
      const arrow = !expandable ? '·' : (isExpanded ? '▼' : '▶');
      const btnAttrs = expandable
        ? `class="aud-bal-expand" data-fecha="${esc(fecha)}" style="background:none;border:none;color:#7d8590;cursor:pointer;padding:0 .35rem"`
        : `style="color:#484f58;padding:0 .35rem" disabled`;

      const parentRow = `<tr>
        <td><button ${btnAttrs}>${arrow}</button>${esc(fecha || '—')}</td>
        <td>${fmt(r.ventaNetaDia)}</td>
        <td>${fmt(r.totalEfectivo)}</td>
        <td>${fmt(r.totalTarjetas)}</td>
        <td>${fmt(r.totalQr)}</td>
        <td>${fmt(r.totalSistecredito)}</td>
        <td>${fmt(r.totalIntercredito)}</td>
        <td>${fmt(r.totalAddi)}</td>
        <td style="text-align:center">${numCierres}</td>
      </tr>`;

      if (!expandable || !isExpanded) return parentRow;
      return parentRow + renderBalanceCajasRow(fecha);
    }).join('');

    return `
      <table class="aud-balance-table">
        <thead><tr>
          <th>Fecha</th><th>Venta neta</th><th>Efectivo</th><th>Tarjetas</th>
          <th>QR</th><th>SisteCr.</th><th>InterCr.</th><th>ADDI</th><th>Cierres</th>
        </tr></thead>
        <tbody>${rows}
          <tr class="aud-balance-totals">
            <td>TOTALES</td>
            <td>${fmt(totVenta)}</td><td>${fmt(totEfectivo)}</td><td>${fmt(totTarjetas)}</td>
            <td>${fmt(totQr)}</td><td>${fmt(totSiste)}</td><td>${fmt(totInter)}</td>
            <td>${fmt(totAddi)}</td><td></td>
          </tr>
        </tbody>
      </table>`;
  }

  // Subfila por caja: tarjetas_dc del Z + conciliacion vinculada (verde si cuadra, diff si no, gris si nada).
  function renderBalanceCajasRow(fecha) {
    const cierresDia = historialItems
      .filter(z => (z.fecha_cierre || z.fecha) === fecha)
      .sort((a, b) => String(a.caja || '').localeCompare(String(b.caja || '')));

    if (!cierresDia.length) {
      return `<tr><td colspan="9" style="background:#0d1117;padding:.6rem 1rem;color:#8b949e;font-style:italic">
        Sin detalle de cierres Z para ${esc(fecha)}.
      </td></tr>`;
    }

    const TOL = 500;
    const filas = cierresDia.map(z => {
      const tarjetas = parseFloat(z.tarjetas_dc || z.tarjetasDc) || 0;
      const efectivo = parseFloat(z.efectivo) || 0;
      // Conciliaciones vinculadas a este cierre Z
      const concs = conciliacionesItems.filter(c => c.auditoriaZId === z.id || c.auditoria_z_id === z.id);

      let concHtml;
      if (!concs.length) {
        if (tarjetas > 0) {
          concHtml = `<span style="color:#f85149;font-style:italic">Sin conciliacion (Tarjetas D/C $${fmt(tarjetas).replace('$ ','')} sin cuadrar)</span>`;
        } else {
          concHtml = `<span style="color:#8b949e;font-style:italic">Sin tarjetas en este cierre</span>`;
        }
      } else {
        concHtml = concs.map(c => {
          const neto = parseFloat(c.montoNeto || c.monto_neto) || 0;
          const diff = tarjetas - neto;
          const ok = Math.abs(diff) < TOL;
          const color = ok ? '#4ade80' : '#f0883e';
          const icon = ok ? '✓' : '!';
          const diffTxt = ok ? '' : ` (diff ${fmt(diff)})`;
          return `<span style="color:${color}">${icon} ${esc(c.proveedor || '—')} ${esc(c.terminalId || c.terminal_id || '—')} → ${fmt(neto)}${diffTxt}</span>`;
        }).join('<br>');
      }

      return `
        <tr style="background:#0d1117">
          <td style="padding-left:2.2rem;color:#c9d1d9">└ Caja ${esc(z.caja || '—')} #${esc(z.numero_cierre ?? z.numeroCierre ?? '—')}</td>
          <td>${fmt(z.total_neto || z.totalNeto)}</td>
          <td>${fmt(efectivo)}</td>
          <td>${fmt(tarjetas)}</td>
          <td>${fmt(z.qr_bancolombia || z.qrBancolombia)}</td>
          <td>${fmt(z.sistecredito)}</td>
          <td>${fmt(z.intercredito)}</td>
          <td>${fmt(z.addi)}</td>
          <td colspan="1" style="font-size:0.875rem">${concHtml}</td>
        </tr>`;
    }).join('');

    return filas;
  }

  async function loadMetricas() {
    if (!businessId) return;
    const res = await AuditoriaApiClient.metricas(businessId);
    if (res.ok) {
      metricasItems = Array.isArray(res.data?.data) ? res.data.data : (Array.isArray(res.data) ? res.data : []);
    }
    const el = container.querySelector('#aud-metricas-content');
    if (el) el.innerHTML = renderMetricas(metricasItems);
  }

  // Actualizar display de validaciones sin re-renderizar todo el formulario
  function updateValidationDisplay() {
    const val = extractedData?.validacion || { checks: [], alertas: [] };

    // Top alertas general
    const topEl = container.querySelector('.aud-alertas-section');
    if (topEl) {
      topEl.outerHTML = renderAlertas(val) || '<div class="aud-alertas-section" style="display:none"></div>';
    } else {
      const form = container.querySelector('#aud-confirm-form');
      if (form && val.alertas.length > 0) {
        form.insertAdjacentHTML('beforebegin', renderAlertas(val));
      }
    }

    // Re-render checks por columna
    container.querySelectorAll('.aud-group-checks').forEach(el => el.remove());

    // Info column
    const infoCol = container.querySelector('.aud-col-info');
    if (infoCol) {
      infoCol.insertAdjacentHTML('beforeend',
        `<div class="aud-group-checks">${renderChecks(val.checks, 'identificacion')}${renderAlertasSeccion(val.alertas, 'identificacion')}</div>`);
    }

    // Money column: agrupar por grupo
    const moneyCol = container.querySelector('.aud-col-money');
    if (moneyCol) {
      ['operaciones', 'adicional', 'medios'].forEach(grupo => {
        const lastRowOfGroup = [...moneyCol.querySelectorAll('.aud-group-sep')]
          .find(el => el.textContent.toLowerCase().includes(
            grupo === 'operaciones' ? 'operaciones' :
            grupo === 'adicional' ? 'adicionales' : 'medios'
          ));
        if (lastRowOfGroup) {
          // Insertar despues del ultimo field row del grupo
          let insertAfter = lastRowOfGroup;
          let next = lastRowOfGroup.nextElementSibling;
          while (next && !next.classList.contains('aud-group-sep')) {
            insertAfter = next;
            next = next.nextElementSibling;
          }
          insertAfter.insertAdjacentHTML('afterend',
            `<div class="aud-group-checks">${renderChecks(val.checks, grupo)}${renderAlertasSeccion(val.alertas, grupo)}</div>`);
        }
      });
    }
  }

  async function loadCierreById(id) {
    const res = await AuditoriaApiClient.getById(id);
    if (!res.ok) {
      avisar('No se pudo cargar el cierre.', { tono: 'error' });
      return;
    }
    const record = res.data?.data || res.data;
    if (!record) return;

    // Mapear de camelCase (API) a snake_case (formulario)
    extractedData = {
      vendedor: record.vendedor,
      fecha: record.fechaCierre || record.fecha_cierre,
      establecimiento: record.establecimiento,
      caja: record.caja,
      hora_cierre: record.horaCierre || record.hora_cierre,
      numero_cierre: record.numeroCierre || record.numero_cierre,
      serie_pos: record.seriePos || record.serie_pos,
      terminal_id: record.terminalId || record.terminal_id,
      num_facturas: record.numFacturas || record.num_facturas,
      num_factura_inicio: record.numFacturaInicio || record.num_factura_inicio,
      num_factura_fin: record.numFacturaFin || record.num_factura_fin,
      num_articulos: record.numArticulos || record.num_articulos,
      stock_vendido: record.stockVendido || record.stock_vendido,
      stock_devuelto: record.stockDevuelto || record.stock_devuelto,
      stock_neto: record.stockNeto || record.stock_neto,
      ventas: record.ventas,
      devolucion: record.devolucion,
      total_neto: record.totalNeto || record.total_neto,
      financiero: record.financiero,
      recogidas: record.recogidas,
      fondo_inicial: record.fondoInicial || record.fondo_inicial,
      descuentos: record.descuentos,
      ventas_gravadas: record.ventasGravadas || record.ventas_gravadas,
      iva_generado: record.ivaGenerado || record.iva_generado,
      ventas_exentas: record.ventasExentas || record.ventas_exentas,
      ventas_excluidas: record.ventasExcluidas || record.ventas_excluidas,
      ca_ttic: record.caTtic || record.ca_ttic,
      efectivo: record.efectivo,
      tarjetas_dc: record.tarjetasDc || record.tarjetas_dc,
      sistecredito: record.sistecredito,
      intercredito: record.intercredito,
      addi: record.addi,
      qr_bancolombia: record.qrBancolombia || record.qr_bancolombia,
      bono_datafono: record.bonoDatafono || record.bono_datafono,
      movimientos: []
    };
    extractedData.validacion = recomputeValidaciones(extractedData);
    extractedTipo = 'cierre_z';
    extractedConfianza = 100;
    phase = 'review';
    activeTab = 'comprobantes';
    render();
  }

  async function loadConciliaciones() {
    if (!businessId) return;
    const res = await AuditoriaApiClient.conciliaciones(businessId);
    if (res.ok) {
      conciliacionesItems = Array.isArray(res.data?.data) ? res.data.data : (Array.isArray(res.data) ? res.data : []);
    }
    const el = container.querySelector('#aud-conc-hist-content');
    if (el) el.innerHTML = renderConciliaciones(conciliacionesItems);
  }

  async function matchConciliacionConZ() {
    const matchEl = container.querySelector('#aud-conc-match');
    if (!matchEl) return;
    const form = container.querySelector('#aud-conc-form');
    if (!form) return;

    const fecha = form.querySelector('[name="fecha"]').value;
    const montoNetoRaw = form.querySelector('[name="monto_neto"]').value;
    const montoNeto = parseFloat(montoNetoRaw) || 0;
    matchedZForConciliacion = null;
    if (!fecha || !montoNeto) {
      matchEl.innerHTML = '';
      return;
    }

    if (!historialItems.length) await loadHistorial();
    const cierresDia = historialItems.filter(r => (r.fecha_cierre || r.fecha) === fecha);

    if (!cierresDia.length) {
      matchEl.innerHTML = `<div class="aud-check-fail">
        <span class="aud-check-icon-fail">!</span>
        <span>No hay cierres Z guardados para ${esc(fecha)}. Sube el cierre primero.</span>
      </div>`;
      return;
    }

    // Por cada cierre del dia, calcular diff individual con la conciliacion.
    // Un datafono cuadra con UNA caja registradora (no con el dia entero), por eso
    // no sumamos: cuando hay 2+ cajas con datafonos distintos, sumar genera falsos negativos.
    const TOL = 500;
    const filas = cierresDia.map(c => {
      const tarjetas = parseFloat(c.tarjetas_dc || c.tarjetasDc) || 0;
      const diff = tarjetas - montoNeto;
      return {
        id: c.id,
        caja: c.caja || '—',
        numero: c.numero_cierre || c.numeroCierre || '—',
        tarjetas,
        diff,
        cuadra: tarjetas > 0 && Math.abs(diff) < TOL,
      };
    });

    const cuadraron = filas.filter(f => f.cuadra);
    matchedZForConciliacion = cuadraron.length === 1 ? cuadraron[0] : null;

    const sumTarjetas = filas.reduce((s, f) => s + f.tarjetas, 0);
    if (sumTarjetas === 0) {
      matchEl.innerHTML = `<div class="aud-check-fail">
        <span class="aud-check-icon-fail">!</span>
        <span>Los cierres Z de ${esc(fecha)} no tienen Tarjetas D/C registradas. Revisa los cierres antes de conciliar.</span>
      </div>`;
      return;
    }

    const headerHtml = (() => {
      if (cuadraron.length === 1) {
        const f = cuadraron[0];
        return `<div class="aud-check-ok">
          <span class="aud-check-icon">OK</span>
          <span>Cuadra con caja ${esc(f.caja)} cierre #${esc(f.numero)} (${fmt(f.tarjetas)}). Se vinculara este cierre al guardar.</span>
        </div>`;
      }
      if (cuadraron.length > 1) {
        return `<div class="aud-check-fail">
          <span class="aud-check-icon-fail">?</span>
          <span>${cuadraron.length} cierres del dia tienen el mismo monto. Revisa cual corresponde al terminal ${esc(form.querySelector('[name="terminal_id"]').value || '—')} antes de guardar.</span>
        </div>`;
      }
      return `<div class="aud-check-fail">
        <span class="aud-check-icon-fail">!</span>
        <span>Ningun cierre individual del dia cuadra con ${fmt(montoNeto)} (tolerancia ${fmt(TOL)}). Verifica monto o sube el cierre Z faltante.</span>
      </div>`;
    })();

    const filasHtml = filas.map(f => {
      const cls = f.cuadra ? 'aud-check-ok' : 'aud-check-fail';
      const icon = f.cuadra ? 'OK' : '·';
      const diffTxt = f.cuadra ? '' : ` <span style="color:#8b949e">(diff ${fmt(f.diff)})</span>`;
      return `<div class="${cls}" style="display:flex;align-items:center;gap:.5rem;margin-top:.25rem;font-size:0.875rem">
        <span class="${f.cuadra ? 'aud-check-icon' : 'aud-check-icon-fail'}">${icon}</span>
        <span>Caja ${esc(f.caja)} — # ${esc(f.numero)} — Tarjetas D/C ${fmt(f.tarjetas)}${diffTxt}</span>
      </div>`;
    }).join('');

    matchEl.innerHTML = `${headerHtml}<div style="margin-top:.5rem">${filasHtml}</div>`;
  }

  async function handleSaveConciliacion() {
    const form = container.querySelector('#aud-conc-form');
    if (!form) return;

    const data = { businessId };
    form.querySelectorAll('input, select').forEach(inp => {
      const k = inp.name;
      if (!k) return;
      const v = inp.type === 'number' ? (inp.value !== '' ? parseFloat(inp.value) : null) : (inp.value || null);
      // Mapear a camelCase para la API
      const camelKey = k.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
      data[camelKey] = v;
    });

    if (!data.fecha || !data.proveedor || !data.montoNeto) {
      avisar('Faltan datos: la fecha, el proveedor y el monto neto son obligatorios.', { tono: 'error' });
      return;
    }

    // Recalcular match para asegurar matchedZForConciliacion vigente con los valores actuales del form.
    await matchConciliacionConZ();

    if (matchedZForConciliacion) {
      data.auditoriaZId = matchedZForConciliacion.id;
    } else {
      // Sin match exacto: avisar antes de guardar para que el usuario decida.
      const ok = await confirmar({
        titulo: 'No cuadra con ningun cierre Z del dia',
        texto: 'Se guarda sin quedar unida a un cierre especifico.',
        aceptar: 'Guardar sin unir'
      });
      if (!ok) return;
      data.auditoriaZId = null;
    }

    const res = await AuditoriaApiClient.saveConciliacion(data);
    if (!res.ok) {
      avisar(`No se pudo guardar la conciliacion: ${res.error?.message || res.error}`, { tono: 'error' });
      return;
    }

    avisar('Conciliacion guardada', { tono: 'ok' });
    form.reset();
    container.querySelectorAll('#aud-conc-form .aud-field-fmt').forEach(el => el.textContent = '');
    await loadConciliaciones();
  }

  // ── Bootstrap ─────────────────────────────────────────────────────────────
  render();
  if (businessId) loadHistorial();

  return container;
}
